// Header.jsx - Liquid Wave Header Component
// Separate Datei für den TEF Promoter Header

import React from 'react';
import { MenuSnowEffect } from './header/EffectsSnow.jsx';
import { MenuFireworkEffect } from './header/EffectsFirework.jsx';
import { CountdownMini, MenuDateDisplay, SilvesterCountdown, getIsoWeek } from './header/Widgets.jsx';


import { MenuCharacter } from './header/MenuCharacter.jsx';
import { MenuWeatherWidget } from './header/MenuWeatherWidget.jsx';
import { QUICK_EVENTS, MENU_BUTTON_REGISTRY, DEFAULT_MENU_BUTTONS } from './header/headerConfig.jsx';

import { HeaderWaves } from './header/HeaderWaves.jsx';
import { HeaderEaster } from './header/HeaderEaster.jsx';
import { HeaderSnowMountains } from './header/HeaderSnowMountains.jsx';
import { MenuButtonGrid } from './header/MenuButtonGrid.jsx';
import { MenuSection } from './header/MenuSection.jsx';
import { SystemMenuSection } from './header/SystemMenuSection.jsx';
// ========== AUSGELAGERTE KOMPONENTEN: MenuCharacter, MenuWeatherWidget ==========
// ========== AUSGELAGERTE KONFIGURATION: QUICK_EVENTS, MENU_BUTTON_REGISTRY ==========

export { MENU_BUTTON_REGISTRY, DEFAULT_MENU_BUTTONS };

function Header({ activeEvent, eventDef, snowIntensity = 50, bubbleIntensity = 50, onEventChange, showBrandLogos = true, showCountdown = true, effects = {}, onEffectsChange, selectedTariffs = [], onRemoveTariff, onClearSelection, onOpenLiveView, appSection = 'dashboard', onAppSectionChange, menuButtons, onHeaderHeight }) {
  // Ausgewählte Tarife vorhanden?
  const hasSelection = selectedTariffs.length > 0;
  
  // Default-Werte für effects
  const effectSettings = {
    headerSnow: effects.headerSnow ?? true,
    cardSnow: effects.cardSnow ?? true,
    cardBubbles: effects.cardBubbles ?? true,
    menuBubbles: effects.menuBubbles ?? true,
    menuFigures: effects.menuFigures ?? true,
    menuFireworks: effects.menuFireworks ?? true,
    headerClock: effects.headerClock ?? true,
    headerWeather: effects.headerWeather ?? true,
    headerDate: effects.headerDate ?? true,
    headerWaves: effects.headerWaves ?? true,
  };
  
  const handleEffectToggle = (key, value) => {
    if (onEffectsChange) {
      onEffectsChange({ ...effectSettings, [key]: value });
    }
  };
  
  const [now, setNow] = React.useState(new Date());
  const [eventMenuOpen, setEventMenuOpen] = React.useState(false);
  const [isScrolled, setIsScrolled] = React.useState(false);
  const [isEventTransitioning, setIsEventTransitioning] = React.useState(false);
  const [displayEvent, setDisplayEvent] = React.useState(activeEvent);
  
  // ========== ACTIVE MENU BUTTONS (max 4, aus Admin-Settings) ==========
  const activeMenuButtons = React.useMemo(() => {
    const ids = (menuButtons && Array.isArray(menuButtons) && menuButtons.length > 0)
      ? menuButtons.slice(0, 4)
      : DEFAULT_MENU_BUTTONS;
    return ids.map(id => MENU_BUTTON_REGISTRY[id]).filter(Boolean);
  }, [menuButtons]);

  // Hauptmenü State
  const [mainMenuOpen, setMainMenuOpen] = React.useState(false);

  const [systemSubmenuOpen, setSystemSubmenuOpen] = React.useState(false);
  const [systemActiveTab, setSystemActiveTab] = React.useState('events'); // 'events' oder 'header'
  const menuStyle = 'particle';
  const mainMenuRef = React.useRef(null);
  const headerWrapperRef = React.useRef(null);

  // Body Scroll Lock wenn Menü offen
  React.useEffect(() => {
    if (mainMenuOpen) {
      document.body.style.overflow = 'hidden';
      document.body.style.touchAction = 'none';
    } else {
      document.body.style.overflow = '';
      document.body.style.touchAction = '';
    }
    return () => { document.body.style.overflow = ''; document.body.style.touchAction = ''; };
  }, [mainMenuOpen]);

  // Header-Höhe messen und an Parent melden
  React.useEffect(() => {
    const el = headerWrapperRef.current;
    if (!el || !onHeaderHeight) return;
    const measure = () => {
      const isCompact = el.classList.contains('header-scrolled');
      let h = 0;
      if (isCompact) {
        // Kompakter Header: feste Höhe
        const compact = el.querySelector('.compact-scroll-header');
        if (compact) h = compact.scrollHeight || 56;
        if (h <= 0) h = 56;
      } else {
        // Voller Header: Backdrop + Wave messen
        const backdrop = el.querySelector('.header-backdrop');
        if (backdrop) {
          const header = backdrop.querySelector('header');
          const waveWrap = backdrop.querySelector('.wave-wrap');
          const snowMountains = backdrop.querySelector('.snow-mountains-wrap');
          const easterMeadow = backdrop.querySelector('.easter-meadow-wrap');
          const headerH = header ? header.offsetHeight : 0;
          const waveH = waveWrap ? waveWrap.offsetHeight : 0;
          const snowH = snowMountains ? snowMountains.offsetHeight : 0;
          const easterH = easterMeadow ? easterMeadow.offsetHeight : 0;
          const cs = getComputedStyle(backdrop);
          const padT = parseFloat(cs.paddingTop) || 0;
          const padB = parseFloat(cs.paddingBottom) || 0;
          h = padT + headerH + Math.max(waveH, snowH, easterH) + padB;
        }
      }
      if (h <= 0 || h > 400) h = isCompact ? 56 : 155;
      onHeaderHeight(h);
    };
    measure();
    if (typeof ResizeObserver !== 'undefined') {
      const ro = new ResizeObserver(() => {
        try {
          if (typeof requestAnimationFrame !== 'undefined') requestAnimationFrame(measure);
          else setTimeout(measure, 0);
        } catch (err) {
          try { setTimeout(measure, 50); } catch (e) { /* ignore */ }
        }
      });
      try { ro.observe(el); } catch (e) { /* ignore observe errors */ }
      return () => { try { ro.disconnect(); } catch (e) { /* ignore */ } };
    }
  }, [onHeaderHeight]);

  // Resize hero labels to fit on a single line by reducing font-size if necessary.
  React.useEffect(() => {
    const fitLabels = () => {
      const labels = Array.from(document.querySelectorAll('.hero-icon-label'));
      labels.forEach(label => {
        if (!label) return;
        // reset any inline sizing before measuring
        label.style.whiteSpace = 'nowrap';
        label.style.fontSize = '';
        label.style.display = 'inline-block';
        // measure
        const parent = label.parentElement;
        if (!parent) return;
        const parentStyle = window.getComputedStyle(parent);
        const parentPaddingLeft = parseFloat(parentStyle.paddingLeft) || 0;
        const parentPaddingRight = parseFloat(parentStyle.paddingRight) || 0;
        const available = parent.clientWidth - parentPaddingLeft - parentPaddingRight - 8; // safety gap

        const computed = window.getComputedStyle(label);
        let fontPx = parseFloat(computed.fontSize) || 14;
        const minFontPx = 10;

        // Shrink until fits or min reached
        label.style.whiteSpace = 'nowrap';
        while (label.scrollWidth > available && fontPx > minFontPx) {
          fontPx = Math.max(minFontPx, fontPx - 1);
          label.style.fontSize = fontPx + 'px';
        }

        // If still too large, allow ellipsis
        if (label.scrollWidth > available) {
          label.style.overflow = 'hidden';
          label.style.textOverflow = 'ellipsis';
          label.style.whiteSpace = 'nowrap';
        } else {
          label.style.overflow = '';
          label.style.textOverflow = '';
        }
      });
    };

    // Run after a short delay to allow fonts to load
    const t = setTimeout(fitLabels, 80);
    window.addEventListener('resize', fitLabels);
    return () => { clearTimeout(t); window.removeEventListener('resize', fitLabels); };
  }, [activeMenuButtons]);
  

  
  // Klick außerhalb schließt Menü
  React.useEffect(() => {
    const handleClickOutside = (e) => {
      if (mainMenuRef.current && !mainMenuRef.current.contains(e.target)) {
        setMainMenuOpen(false);

        setSystemSubmenuOpen(false);
      }
    };
    if (mainMenuOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [mainMenuOpen]);
  const seasonLayerRef = React.useRef(null);
  const snowGroundRef = React.useRef(null);
  const snowLevelRef = React.useRef(0);
  const lastScrollY = React.useRef(0);

  React.useEffect(() => {
    const id = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(id);
  }, []);
  
  // Scroll-Listener für Sticky Header - sanfter Übergang
  React.useEffect(() => {
    const handleScroll = (e) => {
      // Unterstützt window.scroll (normal) und sg-scroll (Spielgeld-Container)
      const currentScrollY = (e && e.detail && e.detail.scrollY != null)
        ? e.detail.scrollY
        : window.scrollY;
      if (currentScrollY > 60 && !isScrolled) {
        setIsScrolled(true);
      } else if (currentScrollY < 30 && isScrolled) {
        setIsScrolled(false);
      }
      lastScrollY.current = currentScrollY;
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    window.addEventListener('sg-scroll', handleScroll, { passive: true });
    return () => {
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('sg-scroll', handleScroll);
    };
  }, [isScrolled]);

  // Re-measure header height when scroll state changes
  React.useEffect(() => {
    if (!onHeaderHeight || !headerWrapperRef.current) return;
    const el = headerWrapperRef.current;
    const measure = () => {
      const isCompact = el.classList.contains('header-scrolled');
      let h = isCompact ? 56 : 0;
      if (!isCompact) {
        const backdrop = el.querySelector('.header-backdrop');
        if (backdrop) {
          const header = backdrop.querySelector('header');
          const waveWrap = backdrop.querySelector('.wave-wrap');
          const snowMountains = backdrop.querySelector('.snow-mountains-wrap');
          const easterMeadow = backdrop.querySelector('.easter-meadow-wrap');
          const headerH = header ? header.offsetHeight : 0;
          const waveH = waveWrap ? waveWrap.offsetHeight : 0;
          const snowH = snowMountains ? snowMountains.offsetHeight : 0;
          const easterH = easterMeadow ? easterMeadow.offsetHeight : 0;
          const cs = getComputedStyle(backdrop);
          const padT = parseFloat(cs.paddingTop) || 0;
          const padB = parseFloat(cs.paddingBottom) || 0;
          h = padT + headerH + Math.max(waveH, snowH, easterH) + padB;
        }
        if (h <= 0 || h > 400) h = 155;
      }
      onHeaderHeight(h);
    };
    // Delay to let CSS transitions settle
    const t1 = setTimeout(measure, 50);
    const t2 = setTimeout(measure, 500);
    return () => { clearTimeout(t1); clearTimeout(t2); };
  }, [isScrolled, onHeaderHeight]);
  
  // Event-Wechsel mit Ausblendeffekt
  React.useEffect(() => {
    if (activeEvent !== displayEvent) {
      setIsEventTransitioning(true);
      // Ausblenden, dann Event wechseln, dann einblenden
      const timeout = setTimeout(() => {
        setDisplayEvent(activeEvent);
        setTimeout(() => {
          setIsEventTransitioning(false);
        }, 100);
      }, 500);
      return () => clearTimeout(timeout);
    }
  }, [activeEvent, displayEvent]);

  const dateText = React.useMemo(() => now.toLocaleDateString("de-DE", {
    weekday: "long",
    day: "2-digit",
    month: "long",
    year: "numeric"
  }), [now]);

  const kwText = React.useMemo(() => "KW: " + getIsoWeek(now).toString().padStart(2, "0"), [now]);
  const timeText = now.toLocaleTimeString("de-DE");

  const appSectionLabel = appSection === 'cards' ? 'Tarife' : appSection === 'spielgeld' ? 'Spielgeldrechner' : appSection === 'bruttonetto' ? 'Brutto/Netto' : 'Dashboard';

  // Realistische Schnee-Berge Wachstum
  const [snowHeight, setSnowHeight] = React.useState(0);
  const snowGrowthRef = React.useRef(0);
  
  // Laub-Haufen Wachstum für Herbst
  const [leafHeight, setLeafHeight] = React.useState(0);
  const leafGrowthRef = React.useRef(0);
  
  // Dynamisches Feuerwerk - mal mehr, mal weniger
  const [fireworks, setFireworks] = React.useState([]);
  const fireworkIdRef = React.useRef(0);
  
  // Schneefallrichtung und Intensität - zufällig wechselnd
  const [snowDirection, setSnowDirection] = React.useState('normal');
  const [dynamicSnowIntensity, setDynamicSnowIntensity] = React.useState(1);
  
  // Event-Typ bestimmen (früh definiert für useEffect-Abhängigkeiten)
  const isChristmasEvent = displayEvent === 'christmas';
  const isWinterEvent = displayEvent === 'winter';
  const isSilvesterEvent = displayEvent === 'silvester';
  const isNewyearEvent = displayEvent === 'newyear';
  const isSnowEvent = isChristmasEvent || isWinterEvent || isSilvesterEvent || isNewyearEvent;
  
  // Feuerwerk dynamisch generieren
  React.useEffect(() => {
    if (!isSilvesterEvent) return;
    
    const colors = ['', 'color-gold', 'color-cyan', 'color-pink', 'color-green', 'color-purple'];
    const positions = [8, 15, 25, 35, 50, 60, 70, 80, 88];
    
    const createFirework = () => {
      const count = 2 + Math.floor(Math.random() * 4); // 2-5 Explosionen
      const newFireworks = [];
      
      for (let i = 0; i < count; i++) {
        newFireworks.push({
          id: fireworkIdRef.current++,
          left: positions[Math.floor(Math.random() * positions.length)] + (Math.random() * 6 - 3),
          top: 5 + Math.random() * 15,
          scale: 0.5 + Math.random() * 0.7,
          color: colors[Math.floor(Math.random() * colors.length)],
          delay: Math.random() * 0.5
        });
      }
      
      setFireworks(newFireworks);
    };
    
    createFirework();
    const interval = setInterval(createFirework, 3000 + Math.random() * 2000);
    
    return () => clearInterval(interval);
  }, [isSilvesterEvent]);
  
  // Schneefallrichtung sanft und kaum merklich wechseln
  const [snowDrift, setSnowDrift] = React.useState(0); // -1 bis 1 für Drift
  
  React.useEffect(() => {
    if (!isSnowEvent) return;
    
    // Sanfter, kaum merklicher Wechsel der Windrichtung
    const changeSnowDrift = () => {
      // Kleine zufällige Änderung (-0.3 bis +0.3) zur aktuellen Richtung
      setSnowDrift(prev => {
        const change = (Math.random() - 0.5) * 0.6;
        const newDrift = Math.max(-1, Math.min(1, prev + change));
        return newDrift;
      });
      
      // Sehr subtile Intensitätsänderung (0.8 - 1.2)
      setDynamicSnowIntensity(0.8 + Math.random() * 0.4);
    };
    
    // Häufiger aber sanfter wechseln
    changeSnowDrift();
    const interval = setInterval(changeSnowDrift, 4000 + Math.random() * 3000); // alle 4-7 Sekunden
    
    return () => clearInterval(interval);
  }, [isSnowEvent]);
  
  // Begrenze das sichtbare Wachstum auf einen moderaten Wert (verhindert Layout-Runaway)
  const MAX_GROWTH = 0.5;
  const increaseSnowMountains = React.useCallback((amount = 0.05) => {
    snowGrowthRef.current = Math.min(snowGrowthRef.current + amount, MAX_GROWTH);
    setSnowHeight(snowGrowthRef.current);
  }, []);

  const increaseLeafPile = React.useCallback((amount = 0.05) => {
    leafGrowthRef.current = Math.min(leafGrowthRef.current + amount, MAX_GROWTH);
    setLeafHeight(leafGrowthRef.current);
  }, []);

  // Season-Effekte im Header (nur im Header-Bereich!)
  React.useEffect(() => {
    const layer = seasonLayerRef.current;
    if (!layer) return;
    
    layer.innerHTML = '';
    snowGrowthRef.current = 0;
    setSnowHeight(0);
    leafGrowthRef.current = 0;
    setLeafHeight(0);

    const snowEvents = ['winter', 'christmas', 'silvester', 'newyear'];
    const leafEvents = []; // Herbst gelöscht
    const confettiEvents = []; // Karneval gelöscht
    const easterEvents = ['easter'];
    const fireworkEvents = [];

    const isSnow = snowEvents.includes(activeEvent);
    const isLeaf = leafEvents.includes(activeEvent);
    const isConfetti = confettiEvents.includes(activeEvent);
    const isEaster = easterEvents.includes(activeEvent);
    const isFirework = fireworkEvents.includes(activeEvent);

    // Debug toggle: allow disabling seasonal effects from global flag (set in tef.html)
    if (window && window.__DISABLE_SEASON_EFFECTS) return;
    if (!isSnow && !isLeaf && !isConfetti && !isEaster && !isFirework) return;

    // SVG Schneeflocken-Formen (als String)
    const snowflakeSVGs = [
      // Klassische 6-zackige Schneeflocke
      `<svg viewBox="0 0 24 24" class="snowflake-svg"><path d="M12 2v20M2 12h20M5.64 5.64l12.72 12.72M18.36 5.64L5.64 18.36" stroke="white" stroke-width="1.5" fill="none" stroke-linecap="round"/><circle cx="12" cy="12" r="2" fill="white"/></svg>`,
      // Detaillierte Schneeflocke
      `<svg viewBox="0 0 24 24" class="snowflake-svg"><path d="M12 2v20M2 12h20M5.64 5.64l12.72 12.72M18.36 5.64L5.64 18.36M12 6l2 2M12 6l-2 2M12 18l2-2M12 18l-2-2M6 12l2 2M6 12l2-2M18 12l-2 2M18 12l-2-2" stroke="white" stroke-width="1" fill="none" stroke-linecap="round"/></svg>`,
      // Stern-Schneeflocke
      `<svg viewBox="0 0 24 24" class="snowflake-svg"><polygon points="12,2 14,9 21,9 15.5,13 17.5,21 12,16 6.5,21 8.5,13 3,9 10,9" fill="white" opacity="0.9"/></svg>`,
      // Einfacher Punkt (abwechselnd)
      `<svg viewBox="0 0 24 24" class="snowflake-svg snowflake-dot"><circle cx="12" cy="12" r="8" fill="white"/></svg>`,
      // Kleiner Punkt
      `<svg viewBox="0 0 24 24" class="snowflake-svg snowflake-dot"><circle cx="12" cy="12" r="10" fill="white"/></svg>`,
      // Kristall-Form
      `<svg viewBox="0 0 24 24" class="snowflake-svg"><path d="M12 2v20M7 7l10 10M17 7l-10 10M2 12h20" stroke="white" stroke-width="1.2" fill="none" stroke-linecap="round"/><circle cx="12" cy="12" r="3" fill="white" opacity="0.8"/><circle cx="12" cy="4" r="1.5" fill="white"/><circle cx="12" cy="20" r="1.5" fill="white"/><circle cx="4" cy="12" r="1.5" fill="white"/><circle cx="20" cy="12" r="1.5" fill="white"/></svg>`,
    ];

    // Limit für gleichzeitige Season-Elemente (verhindert massives DOM-Wachstum)
    const MAX_SEASON_ITEMS = 80;

    // Schneeflocken spawnen - jetzt als SVG
    const spawnSnow = (isBlizzard = false) => {
      if (layer.children.length >= MAX_SEASON_ITEMS) return; // skip wenn schon viele Elemente
      const el = document.createElement('div');
      el.className = 'season-item season-snow-flake';
      const size = (isBlizzard ? 8 : 10) + Math.random() * (isBlizzard ? 10 : 12);
      el.style.width = size + 'px';
      el.style.height = size + 'px';
      el.style.left = Math.random() * 100 + '%';
      
      // Zufällig Schneeflocken-Form oder Punkt wählen
      const svgIndex = Math.floor(Math.random() * snowflakeSVGs.length);
      el.innerHTML = snowflakeSVGs[svgIndex];
      
      // etwas kürzere Dauer bei Blizzard und insgesamt moderater
      const dur = (isBlizzard ? 2.5 : 4) + Math.random() * (isBlizzard ? 1.5 : 2.5);
      const delay = Math.random() * (isBlizzard ? 0.5 : 1.5);
      // Basis-Drift plus kleiner zufälliger Anteil für natürliche Variation
      const baseDrift = (Math.random() > 0.5 ? 1 : -1) * (10 + Math.random() * 20);
      const rotation = Math.random() * 360;
      el.style.setProperty('--fall-duration', dur + 's');
      el.style.setProperty('--fall-delay', delay + 's');
      el.style.setProperty('--drift', baseDrift + 'px');
      el.style.setProperty('--rotation', rotation + 'deg');
      // Jede Flocke bekommt individuellen Drift-Multiplikator für Wind-Effekt
      el.style.setProperty('--drift-multiplier', (baseDrift * 0.5) + 'px');
      layer.appendChild(el);
      
      // Schnee-Berg wächst erst wenn Flocke unten ankommt
      const totalFallTime = (dur + delay) * 1000;
      setTimeout(() => {
        // Flocke ist unten angekommen - Berg minimal wachsen lassen
        if (Math.random() < 0.12) increaseSnowMountains(0.01);
      }, totalFallTime - 500); // 500ms vor Ende für realistisches Timing
      
      setTimeout(() => el.remove(), totalFallTime + 1000);
    };

    // SVG Blätter-Formen für Herbst - REALISTISCHE Blätter
    const leafSVGs = [
      // Realistisches Ahornblatt
      `<svg viewBox="0 0 40 44" class="leaf-svg">
        <defs>
          <linearGradient id="mapleGrad" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" style="stop-color:currentColor;stop-opacity:1" />
            <stop offset="100%" style="stop-color:currentColor;stop-opacity:0.7" />
          </linearGradient>
        </defs>
        <path d="M20 2 L18 8 L12 6 L14 12 L6 12 L12 16 L8 20 L14 20 L12 26 L16 22 L18 28 L20 24 L22 28 L24 22 L28 26 L26 20 L32 20 L28 16 L34 12 L26 12 L28 6 L22 8 L20 2Z" fill="url(#mapleGrad)" stroke="rgba(0,0,0,0.2)" stroke-width="0.3"/>
        <path d="M20 24 L20 42" stroke="rgba(101,67,33,0.8)" stroke-width="2" stroke-linecap="round"/>
        <path d="M16 22 L20 28 M24 22 L20 28 M14 16 L20 22 M26 16 L20 22" stroke="rgba(0,0,0,0.15)" stroke-width="0.5" fill="none"/>
      </svg>`,
      // Realistisches Eichenblatt  
      `<svg viewBox="0 0 32 44" class="leaf-svg">
        <defs>
          <linearGradient id="oakGrad" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" style="stop-color:currentColor;stop-opacity:1" />
            <stop offset="100%" style="stop-color:currentColor;stop-opacity:0.75" />
          </linearGradient>
        </defs>
        <path d="M16 2 C14 4 12 5 10 6 Q6 7 8 10 Q5 11 7 14 Q4 16 7 18 Q5 20 8 22 Q6 25 10 26 C12 28 14 32 16 34 C18 32 20 28 22 26 Q26 25 24 22 Q27 20 25 18 Q28 16 25 14 Q27 11 24 10 Q26 7 22 6 C20 5 18 4 16 2Z" fill="url(#oakGrad)" stroke="rgba(0,0,0,0.2)" stroke-width="0.3"/>
        <path d="M16 34 L16 42" stroke="rgba(101,67,33,0.8)" stroke-width="2" stroke-linecap="round"/>
        <path d="M16 6 L16 30" stroke="rgba(0,0,0,0.12)" stroke-width="0.6" fill="none"/>
        <path d="M10 12 L16 14 M22 12 L16 14 M9 18 L16 20 M23 18 L16 20 M10 24 L16 26 M22 24 L16 26" stroke="rgba(0,0,0,0.1)" stroke-width="0.4" fill="none"/>
      </svg>`,
      // Realistisches Birkenblatt
      `<svg viewBox="0 0 28 40" class="leaf-svg">
        <defs>
          <linearGradient id="birchGrad" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" style="stop-color:currentColor;stop-opacity:1" />
            <stop offset="100%" style="stop-color:currentColor;stop-opacity:0.8" />
          </linearGradient>
        </defs>
        <path d="M14 2 Q8 8 6 14 Q4 20 8 26 Q12 32 14 32 Q16 32 20 26 Q24 20 22 14 Q20 8 14 2Z" fill="url(#birchGrad)" stroke="rgba(0,0,0,0.2)" stroke-width="0.3"/>
        <path d="M14 32 L14 38" stroke="rgba(101,67,33,0.8)" stroke-width="1.5" stroke-linecap="round"/>
        <path d="M14 6 L14 30" stroke="rgba(0,0,0,0.1)" stroke-width="0.5" fill="none"/>
        <path d="M8 12 L14 14 M20 12 L14 14 M7 18 L14 20 M21 18 L14 20 M9 24 L14 26 M19 24 L14 26" stroke="rgba(0,0,0,0.08)" stroke-width="0.3" fill="none"/>
      </svg>`,
      // Realistisches Lindenblatt (herzförmig)
      `<svg viewBox="0 0 30 38" class="leaf-svg">
        <defs>
          <linearGradient id="lindenGrad" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" style="stop-color:currentColor;stop-opacity:1" />
            <stop offset="100%" style="stop-color:currentColor;stop-opacity:0.75" />
          </linearGradient>
        </defs>
        <path d="M15 4 Q6 10 4 18 Q3 26 15 32 Q27 26 26 18 Q24 10 15 4Z" fill="url(#lindenGrad)" stroke="rgba(0,0,0,0.2)" stroke-width="0.3"/>
        <path d="M15 32 L15 36" stroke="rgba(101,67,33,0.8)" stroke-width="1.5" stroke-linecap="round"/>
        <path d="M15 8 L15 30" stroke="rgba(0,0,0,0.1)" stroke-width="0.5" fill="none"/>
        <path d="M8 14 L15 16 M22 14 L15 16 M6 20 L15 22 M24 20 L15 22 M9 26 L15 28 M21 26 L15 28" stroke="rgba(0,0,0,0.08)" stroke-width="0.3" fill="none"/>
      </svg>`,
      // Realistisches Buchenblatt (oval mit gewelltem Rand)
      `<svg viewBox="0 0 26 38" class="leaf-svg">
        <defs>
          <linearGradient id="beechGrad" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" style="stop-color:currentColor;stop-opacity:1" />
            <stop offset="100%" style="stop-color:currentColor;stop-opacity:0.8" />
          </linearGradient>
        </defs>
        <path d="M13 2 Q7 6 5 10 Q3 14 4 18 Q3 22 5 26 Q8 32 13 32 Q18 32 21 26 Q23 22 22 18 Q23 14 21 10 Q19 6 13 2Z" fill="url(#beechGrad)" stroke="rgba(0,0,0,0.2)" stroke-width="0.3"/>
        <path d="M13 32 L13 36" stroke="rgba(101,67,33,0.8)" stroke-width="1.5" stroke-linecap="round"/>
        <path d="M13 6 L13 30" stroke="rgba(0,0,0,0.1)" stroke-width="0.5" fill="none"/>
        <path d="M7 12 L13 14 M19 12 L13 14 M5 18 L13 20 M21 18 L13 20 M7 24 L13 26 M19 24 L13 26" stroke="rgba(0,0,0,0.08)" stroke-width="0.3" fill="none"/>
      </svg>`,
    ];
    const leafColors = ['#c2410c', '#ea580c', '#f59e0b', '#dc2626', '#b91c1c', '#92400e', '#78350f', '#d97706', '#a16207'];

    // Blätter spawnen mit SVG
    const spawnLeaf = () => {
      if (layer.children.length >= MAX_SEASON_ITEMS) return;
      const el = document.createElement('div');
      el.className = 'season-item season-leaf';
      const size = 18 + Math.random() * 16; // Größere Blätter (18-34px)
      el.style.width = size + 'px';
      el.style.height = (size * 1.1) + 'px'; // Leicht länger als breit
      el.style.left = Math.random() * 100 + '%';
      
      // Zufällige Blattform und Farbe
      const svgIndex = Math.floor(Math.random() * leafSVGs.length);
      const color = leafColors[Math.floor(Math.random() * leafColors.length)];
      el.innerHTML = leafSVGs[svgIndex];
      el.style.color = color;
      
      const dur = 6 + Math.random() * 5; // Längere Fallzeit für realistischeres Fallen
      const delay = Math.random() * 2.5;
      const drift = (Math.random() > 0.5 ? 1 : -1) * (40 + Math.random() * 70); // Mehr seitliche Bewegung
      const rotation = Math.random() * 1080 - 540; // Mehr Rotation
      el.style.setProperty('--fall-duration', dur + 's');
      el.style.setProperty('--fall-delay', delay + 's');
      el.style.setProperty('--drift', drift + 'px');
      el.style.setProperty('--rotation', rotation + 'deg');
      layer.appendChild(el);
      
      // Laub-Haufen wächst wenn Blatt unten ankommt
      const totalFallTime = (dur + delay) * 1000;
      setTimeout(() => {
        if (Math.random() < 0.18) increaseLeafPile(0.02);
      }, totalFallTime - 500);
      
      setTimeout(() => el.remove(), totalFallTime + 1000);
    };

    // Konfetti spawnen
    const spawnConfetti = () => {
      if (layer.children.length >= MAX_SEASON_ITEMS) return;
      const el = document.createElement('span');
      el.className = 'season-item season-confetti';
      const colors = activeEvent === 'carnival' 
        ? ['#ff0066', '#00ff66', '#6600ff', '#ffff00', '#ff6600', '#ff00ff']
        : ['#ffd700', '#ff6b6b', '#4ecdc4', '#ffffff', '#ff69b4', '#00ff88'];
      el.style.backgroundColor = colors[Math.floor(Math.random() * colors.length)];
      el.style.left = Math.random() * 100 + '%';
      const dur = 4 + Math.random() * 3;
      const delay = Math.random() * 1.5;
      const drift = (Math.random() > 0.5 ? 1 : -1) * (15 + Math.random() * 35);
      el.style.setProperty('--fall-duration', dur + 's');
      el.style.setProperty('--fall-delay', delay + 's');
      el.style.setProperty('--drift', drift + 'px');
      layer.appendChild(el);
      setTimeout(() => el.remove(), (dur + delay + 1) * 1000);
    };

    // Ostern spawnen
    const spawnEaster = () => {
      if (layer.children.length >= MAX_SEASON_ITEMS) return;
      const el = document.createElement('span');
      el.className = 'season-item season-easter';
      const items = ['🥚', '🐰', '🌷', '🐣', '🌸'];
      el.textContent = items[Math.floor(Math.random() * items.length)];
      el.style.left = Math.random() * 100 + '%';
      const dur = 6 + Math.random() * 4;
      const delay = Math.random() * 1.5;
      const drift = (Math.random() > 0.5 ? 1 : -1) * (12 + Math.random() * 25);
      el.style.setProperty('--fall-duration', dur + 's');
      el.style.setProperty('--fall-delay', delay + 's');
      el.style.setProperty('--drift', drift + 'px');
      layer.appendChild(el);
      setTimeout(() => el.remove(), (dur + delay + 1) * 1000);
    };

    // Feuerwerk spawnen
    const spawnFirework = () => {
      if (layer.children.length >= MAX_SEASON_ITEMS) return;
      const el = document.createElement('span');
      el.className = 'season-item season-firework';
      const items = ['🎆', '🎇', '✨', '💥', '🌟'];
      el.textContent = items[Math.floor(Math.random() * items.length)];
      el.style.left = (10 + Math.random() * 80) + '%';
      el.style.top = (5 + Math.random() * 40) + '%';
      el.style.fontSize = (20 + Math.random() * 30) + 'px';
      layer.appendChild(el);
      setTimeout(() => el.remove(), 2000);
    };

    // Initial spawn
    const initialCount = Math.floor(snowIntensity * 0.3);
    for (let i = 0; i < initialCount; i++) {
      if (isSnow) spawnSnow(false);
      if (isLeaf) spawnLeaf();
      if (isConfetti) spawnConfetti();
      if (isEaster) spawnEaster();
    }

    // Continuous spawn mit variabler Intensität
    let snowPhase = 'normal'; // 'light', 'normal', 'heavy', 'blizzard'
    let phaseTimer = 0;
    const phaseDurations = { light: 8000, normal: 12000, heavy: 6000, blizzard: 3000 };
    
    const getNextPhase = () => {
      const rand = Math.random();
      if (rand < 0.25) return 'light';
      if (rand < 0.65) return 'normal';
      if (rand < 0.90) return 'heavy';
      return 'blizzard';
    };
    
    // Phase wechseln alle paar Sekunden
    const phaseInterval = setInterval(() => {
      snowPhase = getNextPhase();
      phaseTimer = phaseDurations[snowPhase];
    }, 5000 + Math.random() * 8000);
    
    const interval = setInterval(() => {
      if (isSnow) {
        let spawnCount = 1;
        let useBlizzardStyle = false;
        
        switch(snowPhase) {
          case 'light':
            spawnCount = Math.random() < 0.4 ? 1 : 0;
            break;
          case 'normal':
            spawnCount = 1 + (Math.random() < 0.3 ? 1 : 0);
            break;
          case 'heavy':
            spawnCount = 2 + Math.floor(Math.random() * 2);
            break;
          case 'blizzard':
            // weniger aggressive Blizzard-Spawns, damit DOM nicht überläuft
            spawnCount = 2 + Math.floor(Math.random() * 2);
            useBlizzardStyle = true;
            break;
        }
        
        for (let i = 0; i < spawnCount; i++) spawnSnow(useBlizzardStyle);
      }
      if (isLeaf && Math.random() > 0.6) spawnLeaf();
      if (isConfetti) for (let i = 0; i < 2; i++) spawnConfetti();
      if (isEaster && Math.random() > 0.7) spawnEaster();
      if (isFirework && Math.random() > 0.85) spawnFirework();
    }, 500);

    return () => {
      clearInterval(interval);
      clearInterval(phaseInterval);
    };
  }, [activeEvent, snowIntensity, increaseSnowMountains]);

  // Zufällige Seifenblasen - für alle Events
  const [bubbles, setBubbles] = React.useState([]);
  const bubbleIdRef = React.useRef(0);
  
  React.useEffect(() => {
    const spawnBubble = () => {
      const id = bubbleIdRef.current++;
      const size = Math.random();
      let sizeClass = 'hbubble-xxs';
      if (size > 0.95) sizeClass = 'hbubble-lg'; // selten groß
      else if (size > 0.85) sizeClass = 'hbubble-md';
      else if (size > 0.6) sizeClass = 'hbubble-sm';
      else if (size > 0.3) sizeClass = 'hbubble-xs';
      
      const bubble = {
        id,
        left: 5 + Math.random() * 90,
        sizeClass,
        duration: 8 + Math.random() * 10,
        delay: Math.random() * 2
      };
      
      setBubbles(prev => [...prev, bubble]);
      
      // Entferne nach Animation
      setTimeout(() => {
        setBubbles(prev => prev.filter(b => b.id !== id));
      }, (bubble.duration + bubble.delay) * 1000);
    };
    
    // Spawn alle 1.5-4 Sekunden zufällig
    const interval = setInterval(() => {
      if (Math.random() > 0.4) spawnBubble();
    }, 1500 + Math.random() * 2500);
    
    // Initial ein paar spawnen
    for (let i = 0; i < 3; i++) {
      setTimeout(() => spawnBubble(), i * 800);
    }
    
    return () => clearInterval(interval);
  }, []);

  // Wave-Bubbles - steigen von den Wellen unten auf und platzen nach oben hin
  const [waveBubbles, setWaveBubbles] = React.useState([]);
  const waveBubbleIdRef = React.useRef(0);
  
  React.useEffect(() => {
    if (isSnowEvent) {
      setWaveBubbles([]);
      return;
    }
    
    const spawnWaveBubble = () => {
      const id = waveBubbleIdRef.current++;
      const size = Math.random();
      let sizeClass = 'wave-bubble-xs';
      if (size > 0.92) sizeClass = 'wave-bubble-lg';
      else if (size > 0.8) sizeClass = 'wave-bubble-md';
      else if (size > 0.5) sizeClass = 'wave-bubble-sm';
      
      const bubble = {
        id,
        left: 5 + Math.random() * 90,
        sizeClass,
        duration: 5 + Math.random() * 4,
        drift: -15 + Math.random() * 30 // seitliche Drift
      };
      
      setWaveBubbles(prev => [...prev, bubble]);
      
      setTimeout(() => {
        setWaveBubbles(prev => prev.filter(b => b.id !== id));
      }, bubble.duration * 1000);
    };
    
    // Spawn alle 0.8-2 Sekunden
    const interval = setInterval(() => {
      if (Math.random() > 0.3) spawnWaveBubble();
    }, 800 + Math.random() * 1200);
    
    // Initial spawnen
    for (let i = 0; i < 4; i++) {
      setTimeout(() => spawnWaveBubble(), i * 400);
    }
    
    return () => clearInterval(interval);
  }, [isSnowEvent]);

  // Dynamische Sternschnuppen bei Weihnachten - selten erscheinend
  const [shootingStars, setShootingStars] = React.useState([]);
  const shootingStarIdRef = React.useRef(0);
  
  React.useEffect(() => {
    if (!isChristmasEvent) {
      setShootingStars([]);
      return;
    }
    
    const spawnShootingStar = () => {
      const id = shootingStarIdRef.current++;
      const isGold = Math.random() > 0.4; // 60% gold, 40% silber
      const star = {
        id,
        color: isGold ? 'gold' : 'silver',
        startX: 10 + Math.random() * 60, // Startposition 10-70%
        startY: 2 + Math.random() * 15, // Höhe 2-17% von oben
        length: 40 + Math.random() * 60, // Länge 40-100px
        angle: 15 + Math.random() * 25, // Winkel 15-40 Grad
        duration: 1.5 + Math.random() * 1.5, // 1.5-3 Sekunden
        size: 0.6 + Math.random() * 0.6 // Größe 0.6-1.2
      };
      
      setShootingStars(prev => [...prev, star]);
      
      // Nach Animation entfernen
      setTimeout(() => {
        setShootingStars(prev => prev.filter(s => s.id !== id));
      }, star.duration * 1000 + 500);
    };
    
    // Selten spawnen: alle 4-10 Sekunden eine Sternschnuppe
    const interval = setInterval(() => {
      if (Math.random() > 0.5) spawnShootingStar(); // 50% Chance
    }, 4000 + Math.random() * 6000);
    
    // Initial eine spawnen nach kurzer Verzögerung
    setTimeout(() => spawnShootingStar(), 2000);
    
    return () => clearInterval(interval);
  }, [isChristmasEvent]);

  // Weihnachtsmann O2-Puff Symbole
  const [santaPuffs, setSantaPuffs] = React.useState([]);
  const santaPuffIdRef = React.useRef(0);
  
  React.useEffect(() => {
    if (!isChristmasEvent) {
      setSantaPuffs([]);
      return;
    }
    
    const o2Symbols = [
      { type: 'o2', icon: 'O₂' },
      { type: 'wifi', icon: 'wifi' },
      { type: 'phone', icon: 'phone' },
      { type: 'signal', icon: 'signal' },
      { type: 'o2', icon: 'O₂' }
    ];
    
    const spawnSantaPuff = () => {
      const id = santaPuffIdRef.current++;
      const symbol = o2Symbols[Math.floor(Math.random() * o2Symbols.length)];
      
      const puff = {
        id,
        ...symbol,
        size: 14 + Math.random() * 10,
        drift: -20 + Math.random() * 40,
        duration: 3 + Math.random() * 2,
        delay: Math.random() * 0.3
      };
      
      setSantaPuffs(prev => [...prev, puff]);
      
      setTimeout(() => {
        setSantaPuffs(prev => prev.filter(p => p.id !== id));
      }, (puff.duration + puff.delay) * 1000);
    };
    
    // Alle 2-4 Sekunden ein Symbol
    const interval = setInterval(() => {
      if (Math.random() > 0.3) spawnSantaPuff();
    }, 2000 + Math.random() * 2000);
    
    // Initial spawnen
    for (let i = 0; i < 2; i++) {
      setTimeout(() => spawnSantaPuff(), 500 + i * 800);
    }
    
    return () => clearInterval(interval);
  }, [isChristmasEvent]);

  // O2-Logo, my home, Smartphone Bubbles - dezent zufällig aufblitzend (nur wenn aktiviert)
  const [brandLogos, setBrandLogos] = React.useState([]);
  const brandLogoIdRef = React.useRef(0);
  
  React.useEffect(() => {
    if (!showBrandLogos) {
      setBrandLogos([]);
      return;
    }
    
    const spawnBrandLogo = () => {
      const id = brandLogoIdRef.current++;
      const rand = Math.random();
      const typeRand = Math.random();
      
      // Typ bestimmen: O2 häufiger, my home seltener, Smartphone am seltensten
      let logoType = 'o2'; // 60% O2
      if (typeRand > 0.85) logoType = 'smartphone'; // 15% Smartphone
      else if (typeRand > 0.6) logoType = 'myhome'; // 25% my home
      
      // Position: NUR im freien blauen Bereich (oberer Header)
      // Sichere Zonen definieren - diese Bereiche werden gemieden:
      // - Links 0-35%: Menu-Button + Dashboard Titel Bereich - KOMPLETT FREI
      // - Mitte 35-68%: Buttons (Home, Internet etc.)
      // - Rechts 78-100%: Uhrzeit-Widget
      // Erlaubte Zone: NUR 69-77% (rechts vor Uhrzeit) - Menu bleibt frei!
      const safeZones = [
        { start: 69, end: 77 }   // Nur rechte freie Zone - Menu frei halten
      ];
      
      const zone = safeZones[Math.floor(Math.random() * safeZones.length)];
      const leftPos = zone.start + Math.random() * (zone.end - zone.start);
      
      // Zufällige Eigenschaften - NUR im oberen Bereich (55-85% von unten = über den Icons)
      const logo = {
        id,
        logoType,
        left: leftPos,
        bottom: 55 + Math.random() * 30, // 55-85% von unten (im oberen Header-Bereich, über Buttons)
        size: logoType === 'smartphone' ? (14 + Math.random() * 14) : (18 + Math.random() * 20),
        animType: rand > 0.75 ? 'rise' : 'flash',
        duration: rand > 0.75 ? (10 + Math.random() * 5) : (1.8 + Math.random() * 1.5),
        delay: Math.random() * 0.3,
        color: Math.random() > 0.4 ? 'cyan' : 'white'
      };
      
      setBrandLogos(prev => [...prev, logo]);
      
      // Entferne nach Animation
      setTimeout(() => {
        setBrandLogos(prev => prev.filter(l => l.id !== id));
      }, (logo.duration + logo.delay + 1) * 1000);
    };
    
    // Spawn sehr selten und dezent: alle 10-20 Sekunden
    const interval = setInterval(() => {
      if (Math.random() > 0.75) spawnBrandLogo(); // 25% Chance
    }, 10000 + Math.random() * 10000);
    
    // Initial eins nach etwas Zeit
    setTimeout(() => spawnBrandLogo(), 5000);
    
    return () => clearInterval(interval);
  }, [showBrandLogos]);

  // Zeige Schneemann bei Winter/Weihnachten (basierend auf displayEvent für smooth transition)
  const showSnowman = ['winter', 'christmas'].includes(displayEvent);
  // Event-Typ bestimmen - DUPLIZIERTE DEKLARATIONEN ENTFERNT (bereits oben definiert)
  // const isChristmasEvent = displayEvent === 'christmas';
  // const isWinterEvent = displayEvent === 'winter';
  // const isSilvesterEvent = displayEvent === 'silvester';
  // const isNewyearEvent = displayEvent === 'newyear';
  // const isSnowEvent = isChristmasEvent || isWinterEvent || isSilvesterEvent || isNewyearEvent;
  const isEasterEvent = displayEvent === 'easter';
  const isSummerEvent = displayEvent === 'summer';
  const isBlackweekEvent = displayEvent === 'blackweek';
  const isBlackfridayEvent = displayEvent === 'blackfriday';
  const isBlackEvent = isBlackweekEvent || isBlackfridayEvent;
  const isStandardEvent = displayEvent === 'none' || !displayEvent;
  
  // CSS-Klasse für Event-Transition
  const eventTransitionClass = isEventTransitioning ? 'event-scene-exit' : 'event-scene-enter';

  // Schneemann pustet O2-Symbole nach oben
  const [snowmanPuffs, setSnowmanPuffs] = React.useState([]);
  const snowmanPuffIdRef = React.useRef(0);
  
  React.useEffect(() => {
    if (!isWinterEvent || snowHeight < 0.5) {
      setSnowmanPuffs([]);
      return;
    }
    
    const spawnPuff = () => {
      const id = snowmanPuffIdRef.current++;
      const types = ['o2', 'wifi', 'smartphone', 'signal'];
      const puffType = types[Math.floor(Math.random() * types.length)];
      
      const puff = {
        id,
        type: puffType,
        startX: 0, // Startet beim Schneemann-Mund
        drift: -15 + Math.random() * 30, // Leichtes Driften links/rechts
        size: 12 + Math.random() * 10,
        duration: 4 + Math.random() * 3,
        delay: Math.random() * 0.3
      };
      
      setSnowmanPuffs(prev => [...prev, puff]);
      
      // Entferne nach Animation
      setTimeout(() => {
        setSnowmanPuffs(prev => prev.filter(p => p.id !== id));
      }, (puff.duration + puff.delay + 0.5) * 1000);
    };
    
    // Puste alle 2-4 Sekunden
    const interval = setInterval(() => {
      if (Math.random() > 0.3) spawnPuff();
    }, 2000 + Math.random() * 2000);
    
    // Initial ein paar spawnen
    setTimeout(() => spawnPuff(), 500);
    setTimeout(() => spawnPuff(), 1500);
    
    return () => clearInterval(interval);
  }, [isWinterEvent, snowHeight]);

  // Schornstein-Bubbles vom Winterhaus
  const [chimneyBubbles, setChimneyBubbles] = React.useState([]);
  const chimneyBubbleIdRef = React.useRef(0);
  
  React.useEffect(() => {
    if (!isWinterEvent || snowHeight < 0.25) {
      setChimneyBubbles([]);
      return;
    }
    
    const spawnChimneyBubble = () => {
      const id = chimneyBubbleIdRef.current++;
      
      const bubble = {
        id,
        size: 3 + Math.random() * 5,
        drift: -8 + Math.random() * 16,
        duration: 5 + Math.random() * 3,
        delay: Math.random() * 0.5
      };
      
      setChimneyBubbles(prev => [...prev, bubble]);
      
      setTimeout(() => {
        setChimneyBubbles(prev => prev.filter(b => b.id !== id));
      }, (bubble.duration + bubble.delay + 0.5) * 1000);
    };
    
    // Bubbles alle 2-4 Sekunden (weniger häufig)
    const interval = setInterval(() => {
      if (Math.random() > 0.5) spawnChimneyBubble();
    }, 2000 + Math.random() * 2000);
    
    // Initial spawnen - nur einer
    setTimeout(() => spawnChimneyBubble(), 1000);
    
    return () => clearInterval(interval);
  }, [isWinterEvent, snowHeight]);

  // Zufällige kleine Winter-Bäume generieren - unter Menu links
  const winterTrees = React.useMemo(() => {
    const trees = [];
    const treeCount = 3; // 3 Bäume unter Menu
    // Positionen links unter Menu
    const treePositions = [5, 9, 13]; // Links unter Menu
    
    for (let i = 0; i < treeCount; i++) {
      trees.push({
        id: i,
        left: treePositions[i] + (Math.random() * 2 - 1), // Kleine zufällige Variation
        scale: 0.4 + Math.random() * 0.4, // 0.4-0.8 verschiedene Größen
        delay: Math.random() * 0.3,
        opacity: 0.8 + Math.random() * 0.2
      });
    }
    return trees;
  }, []);

  return (
    <>
    <div ref={headerWrapperRef} className={`header-wrapper ${isScrolled ? 'header-scrolled' : ''}`} style={{ position: 'fixed', top: 0, left: 0, right: 0, zIndex: 100 }}>
      {/* Kompakter Scroll-Header - erscheint beim Scrollen */}
      <div className="compact-scroll-header">
        {/* Blur Hintergrund mit Schnee/Blasen */}
        <div className="compact-header-blur-bg">
          {isSnowEvent ? (
            // Schnee im Blur-Hintergrund
            <>
              {[...Array(8)].map((_, i) => (
                <div 
                  key={`csnow-${i}`}
                  className="compact-snow"
                  style={{
                    left: `${10 + i * 12}%`,
                    animationDelay: `${i * 0.5}s`,
                    width: `${3 + Math.random() * 3}px`,
                    height: `${3 + Math.random() * 3}px`
                  }}
                />
              ))}
            </>
          ) : (
            // Seifenblasen im Blur-Hintergrund
            <>
              {[...Array(5)].map((_, i) => (
                <div 
                  key={`cbubble-${i}`}
                  className="compact-bubble"
                  style={{
                    left: `${15 + i * 18}%`,
                    width: `${12 + Math.random() * 10}px`,
                    height: `${12 + Math.random() * 10}px`,
                    animationDelay: `${i * 1}s`
                  }}
                />
              ))}
            </>
          )}
        </div>
        
        {/* Menu Button links — öffnet das Fullscreen-Navigationsmenü */}
        <button className="compact-menu-btn" onClick={() => setMainMenuOpen(true)} title="Menü">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round">
            <path d="M6 7h12" />
            <path d="M6 12h9" />
            <path d="M6 17h6" />
          </svg>
        </button>
        
        {/* Uhrzeit, Datum, KW rechts */}
        <div className="compact-clock-widget">
          <span className="compact-clock-date">{dateText}</span>
          <span className="compact-clock-time">{timeText}</span>
          <span className="compact-clock-kw">{kwText}</span>
        </div>
      </div>
      
      <div className={`header-backdrop pt-0 pb-0 header-main-content ${isBlackEvent ? 'blackweek-header' : ''}`}>
        {/* Season Layer - nur im Header! Mit sanftem Wind-Drift */}
        <div 
          ref={seasonLayerRef} 
          className="season-layer"
          style={isSnowEvent ? { 
            '--snow-intensity': dynamicSnowIntensity,
            '--snow-drift': snowDrift,
            '--wind-angle': `${snowDrift * 15}deg`
          } : {}}
        ></div>
        
        {/* Zufällige Seifenblasen - NICHT bei Winter/Weihnachten */}
        {!isSnowEvent && (
          <div className="pointer-events-none absolute left-0 right-0 bottom-0 h-full overflow-visible header-bubbles-container z-[2]">
            {bubbles.map(b => (
              <span 
                key={b.id}
                className={`hbubble ${b.sizeClass}`}
                style={{
                  left: `${b.left}%`,
                  animation: `hbubbleRiseRandom ${b.duration}s ease-out forwards`,
                  animationDelay: `${b.delay}s`
                }}
            />
          ))}
        </div>
      )}
      
      {/* Brand Logos - O2, my home, Smartphone - dezent zufällig für ALLE Events */}
      <div className="pointer-events-none absolute left-0 right-0 bottom-0 h-full overflow-visible z-[25]">
        {brandLogos.map(logo => (
          <React.Fragment key={logo.id}>
            {/* O2 Logo */}
            {logo.logoType === 'o2' && (
              <svg 
                className={`brand-logo-bubble brand-logo-${logo.animType} brand-logo-${logo.color}`}
                viewBox="0 0 50 30"
                style={{
                  left: `${logo.left}%`,
                  bottom: `${logo.bottom}%`,
                  width: `${logo.size}px`,
                  height: `${logo.size * 0.6}px`,
                  animationDuration: `${logo.duration}s`,
                  animationDelay: `${logo.delay}s`
                }}
              >
                <defs>
                  <linearGradient id={`brandGrad${logo.id}`} x1="0" y1="0" x2="1" y2="1">
                    <stop offset="0%" stopColor={logo.color === 'cyan' ? '#22d3ee' : '#ffffff'} />
                    <stop offset="100%" stopColor={logo.color === 'cyan' ? '#06b6d4' : '#e0f2fe'} />
                  </linearGradient>
                  <filter id={`brandGlow${logo.id}`} x="-50%" y="-50%" width="200%" height="200%">
                    <feGaussianBlur stdDeviation="1.5" result="blur" />
                    <feMerge>
                      <feMergeNode in="blur" />
                      <feMergeNode in="SourceGraphic" />
                    </feMerge>
                  </filter>
                </defs>
                {/* O */}
                <circle cx="12" cy="15" r="9" stroke={`url(#brandGrad${logo.id})`} strokeWidth="2" fill="none" filter={`url(#brandGlow${logo.id})`} />
                {/* 2 */}
                <text x="26" y="21" fill={`url(#brandGrad${logo.id})`} fontSize="16" fontWeight="bold" fontFamily="Arial" filter={`url(#brandGlow${logo.id})`}>2</text>
              </svg>
            )}
            
            {/* my home - Schrift + Home Icon */}
            {logo.logoType === 'myhome' && (
              <svg 
                className={`brand-logo-bubble brand-logo-${logo.animType} brand-logo-${logo.color}`}
                viewBox="0 0 70 35"
                style={{
                  left: `${logo.left}%`,
                  bottom: `${logo.bottom}%`,
                  width: `${logo.size * 1.4}px`,
                  height: `${logo.size * 0.7}px`,
                  animationDuration: `${logo.duration}s`,
                  animationDelay: `${logo.delay}s`
                }}
              >
                <defs>
                  <linearGradient id={`homeGrad${logo.id}`} x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor={logo.color === 'cyan' ? '#67e8f9' : '#ffffff'} />
                    <stop offset="100%" stopColor={logo.color === 'cyan' ? '#06b6d4' : '#e0f2fe'} />
                  </linearGradient>
                  <filter id={`homeGlow${logo.id}`} x="-50%" y="-50%" width="200%" height="200%">
                    <feGaussianBlur stdDeviation="1.5" result="blur" />
                    <feMerge>
                      <feMergeNode in="blur" />
                      <feMergeNode in="SourceGraphic" />
                    </feMerge>
                  </filter>
                </defs>
                {/* "my home" Text */}
                <text x="3" y="14" fill={`url(#homeGrad${logo.id})`} fontSize="10" fontFamily="Arial" fontWeight="500" filter={`url(#homeGlow${logo.id})`}>my</text>
                <text x="3" y="28" fill={`url(#homeGrad${logo.id})`} fontSize="12" fontFamily="Arial" fontWeight="600" filter={`url(#homeGlow${logo.id})`}>home</text>
                {/* Home Icon SVG */}
                <g transform="translate(40, 3)" filter={`url(#homeGlow${logo.id})`}>
                  <path d="M14 2L2 11h3v12h18V11h3L14 2z" stroke={`url(#homeGrad${logo.id})`} strokeWidth="1.2" fill="none" />
                  <rect x="11" y="14" width="6" height="9" rx="0.5" stroke={`url(#homeGrad${logo.id})`} strokeWidth="0.8" fill="none" />
                  <rect x="5" y="13" width="4" height="4" rx="0.3" fill={`url(#homeGrad${logo.id})`} fillOpacity="0.3" />
                  <rect x="19" y="13" width="4" height="4" rx="0.3" fill={`url(#homeGrad${logo.id})`} fillOpacity="0.3" />
                </g>
              </svg>
            )}
            
            {/* Smartphone */}
            {logo.logoType === 'smartphone' && (
              <svg 
                className={`brand-logo-bubble brand-logo-${logo.animType} brand-logo-${logo.color}`}
                viewBox="0 0 24 40"
                style={{
                  left: `${logo.left}%`,
                  bottom: `${logo.bottom}%`,
                  width: `${logo.size * 0.6}px`,
                  height: `${logo.size}px`,
                  animationDuration: `${logo.duration}s`,
                  animationDelay: `${logo.delay}s`
                }}
              >
                <defs>
                  <linearGradient id={`phoneGrad${logo.id}`} x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor={logo.color === 'cyan' ? '#67e8f9' : '#ffffff'} />
                    <stop offset="100%" stopColor={logo.color === 'cyan' ? '#22d3ee' : '#e0f2fe'} />
                  </linearGradient>
                  <filter id={`phoneGlow${logo.id}`} x="-50%" y="-50%" width="200%" height="200%">
                    <feGaussianBlur stdDeviation="1.2" result="blur" />
                    <feMerge>
                      <feMergeNode in="blur" />
                      <feMergeNode in="SourceGraphic" />
                    </feMerge>
                  </filter>
                </defs>
                {/* Smartphone Body */}
                <rect x="2" y="1" width="20" height="38" rx="3" ry="3" stroke={`url(#phoneGrad${logo.id})`} strokeWidth="1.5" fill="none" filter={`url(#phoneGlow${logo.id})`} />
                {/* Screen */}
                <rect x="4" y="5" width="16" height="28" rx="1" fill={`url(#phoneGrad${logo.id})`} fillOpacity="0.15" />
                {/* Speaker */}
                <rect x="9" y="2.5" width="6" height="1" rx="0.5" fill={`url(#phoneGrad${logo.id})`} fillOpacity="0.6" />
                {/* Home Indicator */}
                <rect x="10" y="35" width="4" height="2" rx="1" fill={`url(#phoneGrad${logo.id})`} fillOpacity="0.5" />
              </svg>
            )}
          </React.Fragment>
        ))}
      </div>
      
      {/* Dynamische Sternschnuppen bei Weihnachten - selten erscheinend */}
      {isChristmasEvent && (
        <div className="header-shooting-stars-dynamic">
          <defs style={{display: 'none'}}>
            <svg>
              <linearGradient id="goldTrailDynamic" x1="0" y1="0" x2="1" y2="0">
                <stop offset="0%" stopColor="#fbbf24" stopOpacity="0" />
                <stop offset="70%" stopColor="#fbbf24" stopOpacity="0.8" />
                <stop offset="100%" stopColor="#fef3c7" stopOpacity="1" />
              </linearGradient>
              <linearGradient id="silverTrailDynamic" x1="0" y1="0" x2="1" y2="0">
                <stop offset="0%" stopColor="#e2e8f0" stopOpacity="0" />
                <stop offset="70%" stopColor="#e2e8f0" stopOpacity="0.7" />
                <stop offset="100%" stopColor="#f8fafc" stopOpacity="1" />
              </linearGradient>
            </svg>
          </defs>
          
          {shootingStars.map(star => (
            <svg 
              key={star.id}
              className="shooting-star-dynamic"
              viewBox="0 0 100 20"
              style={{
                left: `${star.startX}%`,
                top: `${star.startY}%`,
                width: `${star.length}px`,
                transform: `rotate(${star.angle}deg) scale(${star.size})`,
                '--duration': `${star.duration}s`
              }}
            >
              <defs>
                <linearGradient id={`trail${star.id}`} x1="0" y1="0" x2="1" y2="0">
                  <stop offset="0%" stopColor={star.color === 'gold' ? '#fbbf24' : '#e2e8f0'} stopOpacity="0" />
                  <stop offset="60%" stopColor={star.color === 'gold' ? '#fbbf24' : '#e2e8f0'} stopOpacity="0.6" />
                  <stop offset="100%" stopColor={star.color === 'gold' ? '#fef3c7' : '#f8fafc'} stopOpacity="1" />
                </linearGradient>
                <filter id={`starGlow${star.id}`} x="-50%" y="-50%" width="200%" height="200%">
                  <feGaussianBlur stdDeviation="2" result="blur" />
                  <feMerge>
                    <feMergeNode in="blur" />
                    <feMergeNode in="SourceGraphic" />
                  </feMerge>
                </filter>
              </defs>
              {/* Schweif */}
              <line x1="0" y1="10" x2="85" y2="10" stroke={`url(#trail${star.id})`} strokeWidth="3" strokeLinecap="round" />
              {/* Stern-Kopf */}
              <circle cx="92" cy="10" r="5" fill={star.color === 'gold' ? '#fbbf24' : '#f8fafc'} filter={`url(#starGlow${star.id})`} />
              {/* Funkeln */}
              <circle cx="92" cy="10" r="2" fill="white" opacity="0.9" />
            </svg>
          ))}
          
          {/* Dezente statische Himmelssterne - funkeln */}
          <svg className="sky-star sky-star-1" viewBox="0 0 16 16">
            <polygon points="8,0 9.5,5.5 16,5.5 11,9 13,16 8,12 3,16 5,9 0,5.5 6.5,5.5" fill="#fef3c7" className="twinkle-star" />
          </svg>
          <svg className="sky-star sky-star-2" viewBox="0 0 12 12">
            <polygon points="6,0 7,4 12,4 8,6.5 9.5,12 6,9 2.5,12 4,6.5 0,4 5,4" fill="#fef9c3" className="twinkle-star" style={{animationDelay: '0.5s'}} />
          </svg>
          <svg className="sky-star sky-star-3" viewBox="0 0 10 10">
            <polygon points="5,0 5.8,3.2 10,3.2 6.6,5.2 7.8,10 5,7.5 2.2,10 3.4,5.2 0,3.2 4.2,3.2" fill="#fefce8" className="twinkle-star" style={{animationDelay: '1s'}} />
          </svg>
        </div>
      )}

      {/* Main Header Content */}
      <header className="w-full relative z-[1]">
        <div className="max-w-6xl mx-auto px-4 sm:px-8 pt-6 pb-2 flex items-center justify-between gap-4">
          {/* Left: Menu & Logo */}
          <div className="flex items-center gap-4">
            {/* Menu Button - öffnet das Fullscreen-Menü */}
            <button 
              className={`header-menu-btn ${mainMenuOpen ? 'menu-active' : ''}`} 
              onClick={() => setMainMenuOpen(!mainMenuOpen)} 
              title="Menü"
            >
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round">
                <path d="M6 7h12" />
                <path d="M6 12h9" />
                <path d="M6 17h6" />
              </svg>
            </button>

            {/* Normal: Logo & Titel / Bei Auswahl: Ausgewählte Tarife */}
            {!hasSelection ? (
              <div className="flex flex-col text-left text-sky-100/90 leading-tight ml-2">
                <div className="flex items-center gap-2">
                  <div className="header-logo-icon" aria-hidden>
                    <svg viewBox="0 0 24 24" className="w-5 h-5" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                      {/* Außenkante: etwas breiter und zentriert */}
                      <rect x="9" y="3" width="6" height="18" rx="2" fill="#34d399" />
                      {/* Display-Innenfläche: zentriert, proportionaler Abstand */}
                      <rect x="10.25" y="5.8" width="3.5" height="10.8" rx="1" fill="#052f2a" />
                      {/* Home-/Fingerprint-Punkt */}
                      <circle cx="12" cy="18.8" r="0.75" fill="#0f172a" />
                    </svg>
                  </div>
                  <span className="text-[0.68rem] tracking-[0.22em] uppercase font-medium">
                    Promotion Telefonica
                  </span>
                </div>
                <span className="text-[1.15rem] font-semibold italic mt-0.5 ml-1">
                  {appSectionLabel}
                </span>
                {/* Silvester Countdown unter Dashboard (nicht bei Neujahr) */}
                {isSilvesterEvent && (
                  <div className="header-countdown-mini">
                    <CountdownMini />
                  </div>
                )}
              </div>
            ) : (
              /* Ausgewählte Tarife im Header */
              <div className="header-selection-view">
                <div className="header-selection-title">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M9 11l3 3L22 4"/>
                    <path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11"/>
                  </svg>
                  <span>{selectedTariffs.length} ausgewählt</span>
                </div>
                <div className="header-selection-chips">
                  {selectedTariffs.slice(0, 5).map(tariff => (
                    <div key={tariff.id} className="header-selection-chip">
                      <span className="chip-name">{tariff.title}</span>
                      <button onClick={() => onRemoveTariff(tariff.id)} className="chip-remove">
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <path d="M18 6L6 18M6 6l12 12"/>
                        </svg>
                      </button>
                    </div>
                  ))}
                  {selectedTariffs.length > 5 && (
                    <span className="header-selection-more">+{selectedTariffs.length - 5} weitere</span>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Black Week/Friday Text */}
          {isBlackEvent && !hasSelection && (
            <div className="blackfriday-header-text">
              <span className="bf-text-line bf-text-black">BLACK</span>
              <span className="bf-text-line bf-text-friday">{isBlackfridayEvent ? 'FRIDAY' : 'WEEK'}</span>
            </div>
          )}

          {/* Right: Clock Widget ODER Selection Actions */}
          <div className="flex items-center gap-3">
            {!hasSelection ? (
              <div className="header-clock-widget">
                <div className="header-clock-badge">
                  <span className="header-clock-time">{timeText}</span>
                </div>
                <span className="header-clock-date">{dateText}</span>
                <span className="header-clock-kw">{kwText}</span>
              </div>
            ) : (
              /* Selection Action Buttons */
              <div className="header-selection-actions">
                <button className="header-sel-btn header-sel-btn-clear" onClick={onClearSelection}>
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M18 6L6 18M6 6l12 12"/>
                  </svg>
                  Alle entfernen
                </button>
                <button className="header-sel-btn header-sel-btn-print" onClick={() => {
                  const ids = selectedTariffs.map(t => t.id).join(',');
                  window.open(`druckansicht.html?tarife=${ids}`, '_blank');
                }}>
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M6 9V2h12v7"/>
                    <path d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2"/>
                    <rect x="6" y="14" width="12" height="8"/>
                  </svg>
                  Drucken
                </button>
                <button className="header-sel-btn header-sel-btn-view" onClick={onOpenLiveView}>
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
                    <circle cx="12" cy="12" r="3"/>
                  </svg>
                  Live-Ansicht
                </button>
              </div>
            )}
          </div>
        </div>
      </header>

      {/* Standard Wave Section - elegante O2-Style Wellen */}
      {!isSnowEvent && !isEasterEvent && !isBlackEvent && <HeaderWaves isScrolled={isScrolled} waveBubbles={waveBubbles} />}

      {isEasterEvent && <HeaderEaster />}

      {isSnowEvent && (
        <div className="snow-mountains-wrap">
          <HeaderSnowMountains snowHeight={snowHeight} isWinterEvent={isWinterEvent} snowmanPuffs={snowmanPuffs} />
          
          {/* O2 Maskottchen - DEAKTIVIERT bis transparentes Bild vorhanden
          <div 
            className="o2-mascots-container"
            style={{
              opacity: Math.min(Math.max((snowHeight - 0.2) * 2, 0), 0.95)
            }}
          >
            <img 
              src="assets/img/o2-mascots-winter.png" 
              alt="O2 Maskottchen" 
              className="o2-mascots-img"
              loading="lazy"
            />
          </div>
          */}
          
          {/* Dynamische SVG Winterlandschaft - NUR bei Winter (nicht Weihnachten) */}
          {isWinterEvent && (
            <div className={`winter-landscape ${eventTransitionClass}`}>
              {/* Kleine weiß leuchtende Bäume auf den Schneebergen verteilt */}
              {winterTrees.map(tree => (
                <svg 
                  key={tree.id}
                  className="winter-glow-tree" 
                  viewBox="0 0 24 32" 
                  style={{
                    left: `${tree.left}%`,
                    bottom: `${4 + snowHeight * 48}px`,
                    width: `${12 + tree.scale * 10}px`,
                    height: `${16 + tree.scale * 14}px`,
                    opacity: Math.min(Math.max((snowHeight - 0.15 - tree.delay * 0.3) * 3, 0), tree.opacity),
                    animationDelay: `${tree.delay}s`
                  }}
                >
                  <defs>
                    <filter id={`treeGlow${tree.id}`} x="-100%" y="-100%" width="300%" height="300%">
                      <feGaussianBlur stdDeviation="2" result="blur" />
                      <feMerge>
                        <feMergeNode in="blur" />
                        <feMergeNode in="SourceGraphic" />
                      </feMerge>
                    </filter>
                  </defs>
                  {/* Kleiner weißer leuchtender Baum */}
                  <polygon 
                    points="12,2 18,12 15,12 20,22 14,22 14,30 10,30 10,22 4,22 9,12 6,12" 
                    fill="white"
                    filter={`url(#treeGlow${tree.id})`}
                  />
                </svg>
              ))}
              
              {/* Winterhaus unter Dashboard - weiß leuchtend - mit Schornstein-Bubbles */}
              <div 
                className="winter-house-container"
                style={{
                  position: 'absolute',
                  left: 'calc(22% + 46px)',
                  bottom: `${4 + snowHeight * 48}px`,
                  width: '42px',
                  height: '60px',
                  opacity: Math.min(Math.max((snowHeight - 0.25) * 2.5, 0), 1)
                }}
              >
                {/* Schornstein-Bubbles */}
                {chimneyBubbles.map(bubble => (
                  <div
                    key={bubble.id}
                    className="chimney-bubble"
                    style={{
                      position: 'absolute',
                      left: '38px',
                      bottom: '32px',
                      width: `${bubble.size}px`,
                      height: `${bubble.size}px`,
                      '--drift': `${bubble.drift}px`,
                      animationDuration: `${bubble.duration}s`,
                      animationDelay: `${bubble.delay}s`
                    }}
                  />
                ))}
                
                <svg 
                  className="winter-house" 
                  viewBox="0 0 50 45"
                  style={{
                    width: '42px',
                    height: '38px'
                  }}
                >
                <defs>
                  <filter id="houseGlowFilter" x="-50%" y="-50%" width="200%" height="200%">
                    <feGaussianBlur stdDeviation="2.5" result="blur" />
                    <feMerge>
                      <feMergeNode in="blur" />
                      <feMergeNode in="SourceGraphic" />
                    </feMerge>
                  </filter>
                </defs>
                {/* Dach */}
                <polygon points="25,2 48,20 2,20" fill="white" filter="url(#houseGlowFilter)" />
                {/* Haus */}
                <rect x="6" y="20" width="38" height="23" fill="white" filter="url(#houseGlowFilter)" />
                {/* Fenster mit warmem Licht */}
                <rect x="10" y="26" width="10" height="8" fill="#fef3c7" className="window-light" opacity="0.9" />
                <rect x="30" y="26" width="10" height="8" fill="#fef3c7" className="window-light" opacity="0.9" style={{animationDelay: '0.5s'}} />
                {/* Tür */}
                <rect x="20" y="30" width="10" height="13" fill="#e0e7ff" opacity="0.8" />
                {/* Schornstein */}
                <rect x="36" y="6" width="6" height="14" fill="white" filter="url(#houseGlowFilter)" />
              </svg>
              </div>
              
              {/* Schneemann bei Winter - weiß leuchtend */}
              <div 
                className="snowman-winter"
                style={{
                  opacity: Math.min(Math.max((snowHeight - 0.2) * 2.5, 0), 1),
                  transform: `scale(${0.7 + snowHeight * 0.4})`
                }}
              >
                <svg viewBox="0 0 45 60">
                  <defs>
                    <filter id="snowmanGlow" x="-50%" y="-50%" width="200%" height="200%">
                      <feGaussianBlur stdDeviation="1.5" result="blur" />
                      <feMerge>
                        <feMergeNode in="blur" />
                        <feMergeNode in="SourceGraphic" />
                      </feMerge>
                    </filter>
                  </defs>
                  {/* Untere Kugel */}
                  <ellipse cx="22.5" cy="48" rx="16" ry="12" fill="#f8fafc" filter="url(#snowmanGlow)" />
                  <ellipse cx="22.5" cy="50" rx="14" ry="8" fill="#e2e8f0" opacity="0.5" />
                  {/* Mittlere Kugel */}
                  <ellipse cx="22.5" cy="32" rx="12" ry="10" fill="#f8fafc" filter="url(#snowmanGlow)" />
                  {/* Kopf */}
                  <circle cx="22.5" cy="16" r="10" fill="#f8fafc" filter="url(#snowmanGlow)" />
                  {/* Augen */}
                  <circle cx="19" cy="14" r="2" fill="#1f2937" />
                  <circle cx="26" cy="14" r="2" fill="#1f2937" />
                  {/* Karotten-Nase */}
                  <polygon points="22.5,17 22.5,19 30,18" fill="#f97316" />
                  {/* Mund */}
                  <circle cx="18" cy="21" r="1" fill="#1f2937" />
                  <circle cx="21" cy="22" r="1" fill="#1f2937" />
                  <circle cx="24" cy="22" r="1" fill="#1f2937" />
                  <circle cx="27" cy="21" r="1" fill="#1f2937" />
                  {/* Hut */}
                  <rect x="14" y="4" width="17" height="3" fill="#1f2937" />
                  <rect x="17" y="-2" width="11" height="7" fill="#1f2937" />
                  {/* Schal - O2-Blau */}
                  <rect x="12" y="24" width="21" height="4" fill="#0ea5e9" rx="1" />
                  <rect x="28" y="24" width="4" height="12" fill="#0ea5e9" rx="1" />
                  {/* Knöpfe */}
                  <circle cx="22.5" cy="35" r="2" fill="#1f2937" />
                  <circle cx="22.5" cy="42" r="2" fill="#1f2937" />
                  {/* Arme */}
                  <line x1="10" y1="32" x2="0" y2="25" stroke="#78350f" strokeWidth="2" strokeLinecap="round" />
                  <line x1="35" y1="32" x2="45" y2="25" stroke="#78350f" strokeWidth="2" strokeLinecap="round" />
                </svg>
              </div>
            </div>
          )}
          
          {/* Weihnachts-Szene bei Christmas - SEPARAT von Winter */}
          {isChristmasEvent && (
            <div className={`christmas-scene ${eventTransitionClass}`}>
              {/* Realistischer Weihnachtsbaum mit Schichten */}
              <svg className="christmas-led-tree" viewBox="0 0 80 100" style={{opacity: Math.min(Math.max((snowHeight - 0.1) * 2.5, 0), 1)}}>
                <defs>
                  {/* Realistische Tannenbaum-Gradienten */}
                  <linearGradient id="treeLayer1" x1="0" y1="0" x2="1" y2="1">
                    <stop offset="0%" stopColor="#1a4d2e" />
                    <stop offset="50%" stopColor="#2d6a4f" />
                    <stop offset="100%" stopColor="#1a4d2e" />
                  </linearGradient>
                  <linearGradient id="treeLayer2" x1="1" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#245c3d" />
                    <stop offset="50%" stopColor="#3a7d5a" />
                    <stop offset="100%" stopColor="#1e5633" />
                  </linearGradient>
                  <linearGradient id="treeLayer3" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#2d6a4f" />
                    <stop offset="100%" stopColor="#1b4332" />
                  </linearGradient>
                  <linearGradient id="trunkGrad" x1="0" y1="0" x2="1" y2="0">
                    <stop offset="0%" stopColor="#5c3d2e" />
                    <stop offset="50%" stopColor="#8b5a3c" />
                    <stop offset="100%" stopColor="#5c3d2e" />
                  </linearGradient>
                  <linearGradient id="starGold" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#fde047" />
                    <stop offset="50%" stopColor="#fbbf24" />
                    <stop offset="100%" stopColor="#f59e0b" />
                  </linearGradient>
                  <filter id="ledGlow" x="-50%" y="-50%" width="200%" height="200%">
                    <feGaussianBlur stdDeviation="2" result="blur" />
                    <feMerge>
                      <feMergeNode in="blur" />
                      <feMergeNode in="SourceGraphic" />
                    </feMerge>
                  </filter>
                  <filter id="treeShadow" x="-10%" y="-10%" width="120%" height="120%">
                    <feDropShadow dx="2" dy="3" stdDeviation="2" floodOpacity="0.3" />
                  </filter>
                </defs>
                
                {/* Stamm mit Textur */}
                <rect x="35" y="82" width="10" height="16" fill="url(#trunkGrad)" rx="1" />
                <rect x="37" y="84" width="2" height="12" fill="#4a3728" opacity="0.3" />
                
                {/* Baumständer/Topf */}
                <ellipse cx="40" cy="96" rx="12" ry="4" fill="#8b4513" />
                <rect x="30" y="92" width="20" height="6" fill="#a0522d" rx="2" />
                
                {/* Unterste Baumschicht (breiteste) */}
                <path d="M40,58 L12,88 Q10,90 14,90 L66,90 Q70,90 68,88 Z" fill="url(#treeLayer1)" filter="url(#treeShadow)" />
                <path d="M40,60 L18,85 L25,82 L40,62 L55,82 L62,85 Z" fill="url(#treeLayer2)" opacity="0.6" />
                
                {/* Mittlere Baumschicht */}
                <path d="M40,38 L18,62 Q16,64 20,64 L60,64 Q64,64 62,62 Z" fill="url(#treeLayer2)" filter="url(#treeShadow)" />
                <path d="M40,40 L24,58 L30,56 L40,42 L50,56 L56,58 Z" fill="url(#treeLayer3)" opacity="0.5" />
                
                {/* Obere Baumschicht */}
                <path d="M40,20 L26,42 Q24,44 28,44 L52,44 Q56,44 54,42 Z" fill="url(#treeLayer1)" filter="url(#treeShadow)" />
                <path d="M40,22 L30,38 L34,36 L40,24 L46,36 L50,38 Z" fill="url(#treeLayer2)" opacity="0.5" />
                
                {/* Spitze */}
                <polygon points="40,6 34,22 46,22" fill="url(#treeLayer3)" />
                
                {/* Goldener Stern oben */}
                <polygon points="40,2 42,7 47,7 43,10 45,15 40,12 35,15 37,10 33,7 38,7" fill="url(#starGold)" filter="url(#ledGlow)" className="led-star" />
                
                {/* Schnee auf den Ästen */}
                <ellipse cx="20" cy="64" rx="6" ry="2" fill="white" opacity="0.8" />
                <ellipse cx="60" cy="64" rx="6" ry="2" fill="white" opacity="0.8" />
                <ellipse cx="16" cy="88" rx="7" ry="2.5" fill="white" opacity="0.8" />
                <ellipse cx="64" cy="88" rx="7" ry="2.5" fill="white" opacity="0.8" />
                <ellipse cx="28" cy="44" rx="5" ry="1.5" fill="white" opacity="0.8" />
                <ellipse cx="52" cy="44" rx="5" ry="1.5" fill="white" opacity="0.8" />
                
                {/* LED Lichter - verteilt */}
                <circle cx="25" cy="75" r="2.5" fill="#ef4444" filter="url(#ledGlow)" className="led-light led-red" />
                <circle cx="55" cy="72" r="2.5" fill="#3b82f6" filter="url(#ledGlow)" className="led-light led-blue" style={{animationDelay: '0.2s'}} />
                <circle cx="30" cy="55" r="2.5" fill="#22c55e" filter="url(#ledGlow)" className="led-light led-green" style={{animationDelay: '0.4s'}} />
                <circle cx="50" cy="58" r="2.5" fill="#fbbf24" filter="url(#ledGlow)" className="led-light led-yellow" style={{animationDelay: '0.6s'}} />
                <circle cx="40" cy="50" r="2.5" fill="#ec4899" filter="url(#ledGlow)" className="led-light led-pink" style={{animationDelay: '0.8s'}} />
                <circle cx="20" cy="82" r="2.5" fill="#ef4444" filter="url(#ledGlow)" className="led-light led-red" style={{animationDelay: '1s'}} />
                <circle cx="60" cy="80" r="2.5" fill="#3b82f6" filter="url(#ledGlow)" className="led-light led-blue" style={{animationDelay: '1.2s'}} />
                <circle cx="35" cy="35" r="2.5" fill="#22c55e" filter="url(#ledGlow)" className="led-light led-green" style={{animationDelay: '1.4s'}} />
                <circle cx="45" cy="38" r="2.5" fill="#fbbf24" filter="url(#ledGlow)" className="led-light led-yellow" style={{animationDelay: '1.6s'}} />
                <circle cx="40" cy="68" r="2.5" fill="#ec4899" filter="url(#ledGlow)" className="led-light led-pink" style={{animationDelay: '1.8s'}} />
                <circle cx="32" cy="82" r="2" fill="#06b6d4" filter="url(#ledGlow)" className="led-light led-cyan" style={{animationDelay: '2s'}} />
                <circle cx="48" cy="85" r="2" fill="#a855f7" filter="url(#ledGlow)" className="led-light led-purple" style={{animationDelay: '2.2s'}} />
                
                {/* Kugeln / Ornamente */}
                <circle cx="22" cy="68" r="3.5" fill="#dc2626" stroke="#991b1b" strokeWidth="0.5" />
                <ellipse cx="21" cy="67" rx="1" ry="0.8" fill="white" opacity="0.4" />
                <circle cx="58" cy="65" r="3.5" fill="#2563eb" stroke="#1d4ed8" strokeWidth="0.5" />
                <ellipse cx="57" cy="64" rx="1" ry="0.8" fill="white" opacity="0.4" />
                <circle cx="38" cy="78" r="4" fill="#d97706" stroke="#b45309" strokeWidth="0.5" />
                <ellipse cx="37" cy="77" rx="1.2" ry="1" fill="white" opacity="0.4" />
                <circle cx="52" cy="48" r="3" fill="#7c3aed" stroke="#6d28d9" strokeWidth="0.5" />
                <ellipse cx="51" cy="47" rx="0.8" ry="0.6" fill="white" opacity="0.4" />
                <circle cx="28" cy="52" r="3" fill="#059669" stroke="#047857" strokeWidth="0.5" />
                <ellipse cx="27" cy="51" rx="0.8" ry="0.6" fill="white" opacity="0.4" />
              </svg>
              
              {/* Weihnachtsmann Container mit O2 Symbolen */}
              <div className="santa-o2-container" style={{opacity: Math.min(Math.max((snowHeight - 0.15) * 2, 0), 1)}}>
                <svg className="santa-modern-inner" viewBox="0 0 70 90">
                  <defs>
                    <linearGradient id="santaCoatMod" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#dc2626" />
                      <stop offset="100%" stopColor="#991b1b" />
                    </linearGradient>
                    <linearGradient id="santaSkin" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#fde1c9" />
                      <stop offset="100%" stopColor="#f5d0b5" />
                    </linearGradient>
                    <filter id="santaShadow" x="-20%" y="-20%" width="140%" height="140%">
                      <feDropShadow dx="1" dy="2" stdDeviation="2" floodOpacity="0.3" />
                    </filter>
                  </defs>
                  
                  {/* Beine/Stiefel */}
                  <ellipse cx="28" cy="85" rx="8" ry="5" fill="#1f2937" />
                  <ellipse cx="42" cy="85" rx="8" ry="5" fill="#1f2937" />
                  
                  {/* Körper */}
                  <ellipse cx="35" cy="60" rx="20" ry="22" fill="url(#santaCoatMod)" filter="url(#santaShadow)" />
                  
                  {/* Gürtel */}
                  <rect x="15" y="58" width="40" height="5" fill="#1f2937" />
                  <rect x="31" y="56" width="8" height="9" fill="#fbbf24" rx="1" />
                  
                  {/* Pelzrand unten */}
                  <ellipse cx="35" cy="80" rx="22" ry="5" fill="#f8fafc" />
                  
                  {/* Kopf */}
                  <ellipse cx="35" cy="28" rx="14" ry="16" fill="url(#santaSkin)" />
                  
                  {/* Bart - voluminöser */}
                  <ellipse cx="35" cy="40" rx="13" ry="12" fill="#f8fafc" />
                  <ellipse cx="35" cy="48" rx="10" ry="8" fill="#f1f5f9" />
                  <ellipse cx="35" cy="52" rx="6" ry="4" fill="#e2e8f0" />
                  
                  {/* Augen - freundlich */}
                  <ellipse cx="30" cy="25" rx="2" ry="2.5" fill="#1f2937" />
                  <ellipse cx="40" cy="25" rx="2" ry="2.5" fill="#1f2937" />
                  <circle cx="29" cy="24" r="0.8" fill="white" />
                  <circle cx="39" cy="24" r="0.8" fill="white" />
                  
                  {/* Augenbrauen */}
                  <path d="M27 21 Q30 19 33 21" stroke="#f8fafc" strokeWidth="2" fill="none" strokeLinecap="round" />
                  <path d="M37 21 Q40 19 43 21" stroke="#f8fafc" strokeWidth="2" fill="none" strokeLinecap="round" />
                  
                  {/* Nase */}
                  <ellipse cx="35" cy="30" rx="3.5" ry="3" fill="#fca5a5" />
                  
                  {/* Wangen */}
                  <ellipse cx="26" cy="32" rx="3" ry="2.5" fill="#fecaca" opacity="0.5" />
                  <ellipse cx="44" cy="32" rx="3" ry="2.5" fill="#fecaca" opacity="0.5" />
                  
                  {/* Lächeln */}
                  <path d="M30 36 Q35 40 40 36" stroke="#b91c1c" strokeWidth="1.5" fill="none" strokeLinecap="round" />
                  
                  {/* Schnurrbart */}
                  <path d="M29 35 Q22 34 20 37" stroke="#f8fafc" strokeWidth="3" fill="none" strokeLinecap="round" />
                  <path d="M41 35 Q48 34 50 37" stroke="#f8fafc" strokeWidth="3" fill="none" strokeLinecap="round" />
                  
                  {/* Mütze */}
                  <path d="M20 22 Q22 5 35 3 Q48 5 50 22" fill="#dc2626" />
                  <ellipse cx="35" cy="22" rx="17" ry="4" fill="#f8fafc" />
                  
                  {/* Mützen-Spitze */}
                  <path d="M48 15 Q55 8 58 12" stroke="#dc2626" strokeWidth="6" fill="none" strokeLinecap="round" />
                  <circle cx="58" cy="12" r="5" fill="#f8fafc" />
                  
                  {/* Linker Arm */}
                  <ellipse cx="17" cy="52" rx="5" ry="12" fill="url(#santaCoatMod)" />
                  <ellipse cx="14" cy="64" rx="4" ry="3" fill="#f8fafc" />
                  
                  {/* Rechter Arm mit Handy - bewegt sich */}
                  <g className="santa-phone-arm">
                    {/* Arm */}
                    <ellipse cx="52" cy="48" rx="5" ry="11" fill="url(#santaCoatMod)" transform="rotate(-15 52 48)" />
                    {/* Manschette */}
                    <ellipse cx="58" cy="38" rx="4" ry="3" fill="#f8fafc" />
                    {/* Hand */}
                    <ellipse cx="60" cy="34" rx="4" ry="5" fill="url(#santaSkin)" />
                    {/* Daumen */}
                    <ellipse cx="56" cy="32" rx="1.5" ry="2.5" fill="url(#santaSkin)" />
                    
                    {/* Smartphone in der Hand */}
                    <g className="santa-phone">
                      <defs>
                        <linearGradient id="phoneScreen" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="0%" stopColor="#0ea5e9" />
                          <stop offset="100%" stopColor="#0284c7" />
                        </linearGradient>
                        <filter id="phoneGlow" x="-50%" y="-50%" width="200%" height="200%">
                          <feGaussianBlur stdDeviation="1" result="blur" />
                          <feMerge>
                            <feMergeNode in="blur" />
                            <feMergeNode in="SourceGraphic" />
                          </feMerge>
                        </filter>
                      </defs>
                      {/* Handy Gehäuse */}
                      <rect x="58" y="22" width="10" height="18" rx="2" fill="#1f2937" stroke="#374151" strokeWidth="0.5" />
                      {/* Display */}
                      <rect x="59.5" y="24" width="7" height="13" rx="1" fill="url(#phoneScreen)" filter="url(#phoneGlow)" />
                      {/* O2 Logo auf Display */}
                      <text x="61" y="32" fill="white" fontSize="5" fontWeight="bold">O₂</text>
                      {/* Kamera oben */}
                      <circle cx="63" cy="23" r="0.8" fill="#374151" />
                      {/* Home-Button Bereich */}
                      <rect x="61" y="38" width="4" height="1" rx="0.5" fill="#374151" />
                    </g>
                  </g>
                </svg>
                
                {/* O2 Symbole die vom Santa aufsteigen */}
                <div className="o2-bubbles santa-bubbles">
                  <div className="o2-bubble bubble-1 neon-white">
                    <svg viewBox="0 0 50 30"><text x="5" y="22" fill="currentColor" fontSize="14" fontWeight="bold">O₂</text></svg>
                  </div>
                  <div className="o2-bubble bubble-2 neon-cyan">
                    <svg viewBox="0 0 24 24" fill="currentColor"><path d="M12 18c1.1 0 2 .9 2 2s-.9 2-2 2-2-.9-2-2 .9-2 2-2zm-4.9-2.3l1.4 1.4C9.4 16.4 10.6 16 12 16s2.6.4 3.5 1.1l1.4-1.4C15.6 14.6 13.9 14 12 14s-3.6.6-4.9 1.7z"/></svg>
                  </div>
                  <div className="o2-bubble bubble-3 neon-white">
                    <svg viewBox="0 0 24 24" fill="currentColor"><rect x="7" y="2" width="10" height="20" rx="2" stroke="currentColor" strokeWidth="1.5" fill="none"/><circle cx="12" cy="18" r="1.5" fill="currentColor"/></svg>
                  </div>
                  <div className="o2-bubble bubble-4 neon-cyan">
                    <svg viewBox="0 0 50 30"><text x="5" y="22" fill="currentColor" fontSize="14" fontWeight="bold">O₂</text></svg>
                  </div>
                </div>
              </div>
              
              {/* Geschenke um Baum und Santa */}
              <div className="christmas-gifts" style={{opacity: Math.min(Math.max((snowHeight - 0.2) * 2.5, 0), 1)}}>
                {/* Geschenk 1 - rot mit goldener Schleife */}
                <svg className="gift gift-1" viewBox="0 0 24 20">
                  <rect x="2" y="6" width="20" height="14" rx="1" fill="#dc2626" />
                  <rect x="2" y="6" width="20" height="3" fill="#b91c1c" />
                  <rect x="10" y="6" width="4" height="14" fill="#fbbf24" />
                  <ellipse cx="12" cy="6" rx="5" ry="3" fill="#fbbf24" />
                  <ellipse cx="9" cy="4" rx="2" ry="2.5" fill="#fbbf24" />
                  <ellipse cx="15" cy="4" rx="2" ry="2.5" fill="#fbbf24" />
                </svg>
                
                {/* Geschenk 2 - blau mit weißer Schleife */}
                <svg className="gift gift-2" viewBox="0 0 20 24">
                  <rect x="2" y="7" width="16" height="16" rx="1" fill="#2563eb" />
                  <rect x="2" y="7" width="16" height="3" fill="#1d4ed8" />
                  <rect x="8" y="7" width="4" height="16" fill="#f8fafc" />
                  <ellipse cx="10" cy="7" rx="4" ry="2.5" fill="#f8fafc" />
                  <ellipse cx="7" cy="5" rx="2" ry="2" fill="#f8fafc" />
                  <ellipse cx="13" cy="5" rx="2" ry="2" fill="#f8fafc" />
                </svg>
                
                {/* Geschenk 3 - grün mit roter Schleife */}
                <svg className="gift gift-3" viewBox="0 0 18 16">
                  <rect x="1" y="5" width="16" height="10" rx="1" fill="#16a34a" />
                  <rect x="1" y="5" width="16" height="2" fill="#15803d" />
                  <rect x="7" y="5" width="4" height="10" fill="#ef4444" />
                  <ellipse cx="9" cy="5" rx="3" ry="2" fill="#ef4444" />
                  <ellipse cx="7" cy="3.5" rx="1.5" ry="1.8" fill="#ef4444" />
                  <ellipse cx="11" cy="3.5" rx="1.5" ry="1.8" fill="#ef4444" />
                </svg>
                
                {/* Geschenk 4 - lila mit goldener Schleife */}
                <svg className="gift gift-4" viewBox="0 0 16 14">
                  <rect x="1" y="4" width="14" height="9" rx="1" fill="#9333ea" />
                  <rect x="1" y="4" width="14" height="2" fill="#7e22ce" />
                  <rect x="6" y="4" width="3" height="9" fill="#fbbf24" />
                  <ellipse cx="7.5" cy="4" rx="2.5" ry="1.8" fill="#fbbf24" />
                  <ellipse cx="6" cy="2.8" rx="1.2" ry="1.5" fill="#fbbf24" />
                  <ellipse cx="9" cy="2.8" rx="1.2" ry="1.5" fill="#fbbf24" />
                </svg>
              </div>
            </div>
          )}
        </div>
      )}

      {/* ========== BLACK WEEK/FRIDAY SECTION - Neonpinke Welle ========== */}
      {isBlackEvent && (
        <div className="blackweek-wave-wrap">
          <svg viewBox="0 0 1440 60" className="blackweek-wave-svg" preserveAspectRatio="none">
            <defs>
              <linearGradient id="neonPinkWave1" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#ff0080" stopOpacity="1"/>
                <stop offset="50%" stopColor="#ff00aa" stopOpacity="0.8"/>
                <stop offset="100%" stopColor="#ff00ff" stopOpacity="0.5"/>
              </linearGradient>
              <linearGradient id="neonPinkWave2" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#ff00ff" stopOpacity="0.7"/>
                <stop offset="100%" stopColor="#cc0066" stopOpacity="0.3"/>
              </linearGradient>
              <linearGradient id="neonPinkWave3" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#ff0066" stopOpacity="0.5"/>
                <stop offset="100%" stopColor="#990044" stopOpacity="0.2"/>
              </linearGradient>
              <filter id="neonWaveGlow" x="-10%" y="-100%" width="120%" height="300%">
                <feGaussianBlur stdDeviation="4" result="blur"/>
                <feMerge>
                  <feMergeNode in="blur"/>
                  <feMergeNode in="blur"/>
                  <feMergeNode in="SourceGraphic"/>
                </feMerge>
              </filter>
            </defs>
            {/* Hinterste Welle - am dunkelsten */}
            <path 
              d="M0,35 Q120,15 240,30 T480,20 T720,35 T960,22 T1200,32 T1440,25 L1440,60 L0,60 Z" 
              fill="url(#neonPinkWave3)"
              className="bw-wave-back"
            />
            {/* Mittlere Welle */}
            <path 
              d="M0,40 Q150,22 300,38 T600,25 T900,40 T1200,28 T1440,35 L1440,60 L0,60 Z" 
              fill="url(#neonPinkWave2)"
              className="bw-wave-mid"
            />
            {/* Vorderste Welle - Neon Glow */}
            <path 
              d="M0,45 Q180,30 360,42 T720,32 T1080,45 T1440,38 L1440,60 L0,60 Z" 
              fill="url(#neonPinkWave1)"
              filter="url(#neonWaveGlow)"
              className="bw-wave-front"
            />
          </svg>
        </div>
      )}
      
      {/* ========== WINTERBÄUME RECHTS - für alle Winter-Events ========== */}
      {isSnowEvent && (
        <div className="christmas-right-trees" style={{opacity: Math.min(Math.max((snowHeight - 0.15) * 2.5, 0), 1)}}>
          {/* Baum 1 - groß, rechts außen */}
          <svg className="right-tree right-tree-1" viewBox="0 0 30 45">
            <defs>
              <filter id="winterTreeGlow1" x="-50%" y="-50%" width="200%" height="200%">
                <feGaussianBlur stdDeviation="2" result="blur" />
                <feMerge><feMergeNode in="blur" /><feMergeNode in="SourceGraphic" /></feMerge>
              </filter>
            </defs>
            <polygon points="15,3 26,18 22,18 28,30 20,30 24,40 6,40 10,30 2,30 8,18 4,18" fill="white" filter="url(#winterTreeGlow1)" />
          </svg>
          
          {/* Baum 2 - mittel */}
          <svg className="right-tree right-tree-2" viewBox="0 0 24 36">
            <defs>
              <filter id="winterTreeGlow2" x="-50%" y="-50%" width="200%" height="200%">
                <feGaussianBlur stdDeviation="1.5" result="blur" />
                <feMerge><feMergeNode in="blur" /><feMergeNode in="SourceGraphic" /></feMerge>
              </filter>
            </defs>
            <polygon points="12,2 21,14 17,14 22,24 16,24 20,32 4,32 8,24 2,24 7,14 3,14" fill="white" filter="url(#winterTreeGlow2)" />
          </svg>
          
          {/* Baum 3 - klein */}
          <svg className="right-tree right-tree-3" viewBox="0 0 20 28">
            <defs>
              <filter id="winterTreeGlow3" x="-50%" y="-50%" width="200%" height="200%">
                <feGaussianBlur stdDeviation="1.5" result="blur" />
                <feMerge><feMergeNode in="blur" /><feMergeNode in="SourceGraphic" /></feMerge>
              </filter>
            </defs>
            <polygon points="10,2 17,12 14,12 18,22 2,22 6,12 3,12" fill="white" filter="url(#winterTreeGlow3)" />
          </svg>
          
          {/* Baum 4 - mittelgroß */}
          <svg className="right-tree right-tree-4" viewBox="0 0 26 38">
            <defs>
              <filter id="winterTreeGlow4" x="-50%" y="-50%" width="200%" height="200%">
                <feGaussianBlur stdDeviation="1.8" result="blur" />
                <feMerge><feMergeNode in="blur" /><feMergeNode in="SourceGraphic" /></feMerge>
              </filter>
            </defs>
            <polygon points="13,2 22,15 18,15 24,26 17,26 21,34 5,34 9,26 2,26 8,15 4,15" fill="white" filter="url(#winterTreeGlow4)" />
          </svg>
          
          {/* Baum 5 - klein hinten */}
          <svg className="right-tree right-tree-5" viewBox="0 0 18 24">
            <defs>
              <filter id="winterTreeGlow5" x="-50%" y="-50%" width="200%" height="200%">
                <feGaussianBlur stdDeviation="1.2" result="blur" />
                <feMerge><feMergeNode in="blur" /><feMergeNode in="SourceGraphic" /></feMerge>
              </filter>
            </defs>
            <polygon points="9,1 15,10 12,10 16,18 2,18 6,10 3,10" fill="white" filter="url(#winterTreeGlow5)" />
          </svg>
          
          {/* Baum 6 - mittel hinten */}
          <svg className="right-tree right-tree-6" viewBox="0 0 22 32">
            <defs>
              <filter id="winterTreeGlow6" x="-50%" y="-50%" width="200%" height="200%">
                <feGaussianBlur stdDeviation="1.5" result="blur" />
                <feMerge><feMergeNode in="blur" /><feMergeNode in="SourceGraphic" /></feMerge>
              </filter>
            </defs>
            <polygon points="11,2 18,12 15,12 20,22 14,22 18,30 4,30 8,22 2,22 7,12 4,12" fill="white" filter="url(#winterTreeGlow6)" />
          </svg>
        </div>
      )}

      {/* ========== SILVESTER EVENT ========== */}
      {isSilvesterEvent && (
        <div className="event-scene silvester-scene">
          {/* Dynamisches Feuerwerk - zufällige Anzahl und Positionen */}
          <div className={`fireworks-top snow-${snowDirection}`}>
            {fireworks.map(fw => (
              <div 
                key={fw.id}
                className={`explosion-only ${fw.color}`}
                style={{
                  left: `${fw.left}%`,
                  top: `${fw.top}%`,
                  transform: `scale(${fw.scale})`,
                  animationDelay: `${fw.delay}s`
                }}
              >
                <span className="spark"></span><span className="spark"></span><span className="spark"></span>
                <span className="spark"></span><span className="spark"></span><span className="spark"></span>
                <span className="spark"></span><span className="spark"></span><span className="spark"></span>
                <span className="spark"></span><span className="spark"></span><span className="spark"></span>
              </div>
            ))}
          </div>
          
          {/* Glücksschwein unten links - pustet O2 Seifenblasen */}
          <div className="lucky-pig">
            {/* O2-Bubbles die nach oben pusten */}
            <div className="pig-o2-bubbles">
              <svg className="pig-bubble pig-bubble-1" viewBox="0 0 30 30" fill="none">
                <circle cx="15" cy="15" r="12" fill="rgba(0,180,255,0.15)" stroke="rgba(0,200,255,0.5)" strokeWidth="1.5"/>
                <ellipse cx="11" cy="11" rx="3" ry="2" fill="rgba(255,255,255,0.3)"/>
                <text x="8" y="19" fill="rgba(0,220,255,0.9)" fontSize="8" fontWeight="bold">O₂</text>
              </svg>
              <svg className="pig-bubble pig-bubble-2" viewBox="0 0 30 30" fill="none">
                <circle cx="15" cy="15" r="10" fill="rgba(0,180,255,0.12)" stroke="rgba(0,200,255,0.4)" strokeWidth="1"/>
                <ellipse cx="11" cy="11" rx="2.5" ry="1.5" fill="rgba(255,255,255,0.25)"/>
                <text x="9" y="18" fill="rgba(0,220,255,0.85)" fontSize="7" fontWeight="bold">O₂</text>
              </svg>
              <svg className="pig-bubble pig-bubble-3" viewBox="0 0 30 30" fill="none">
                <circle cx="15" cy="15" r="14" fill="rgba(0,180,255,0.18)" stroke="rgba(0,200,255,0.55)" strokeWidth="1.8"/>
                <ellipse cx="10" cy="10" rx="3.5" ry="2.2" fill="rgba(255,255,255,0.35)"/>
                <text x="7" y="20" fill="rgba(0,220,255,0.95)" fontSize="9" fontWeight="bold">O₂</text>
              </svg>
            </div>
            <svg viewBox="0 0 80 90" className="pig-svg">
              {/* Körper */}
              <ellipse cx="40" cy="55" rx="28" ry="25" fill="#fda4af" />
              <ellipse cx="40" cy="60" rx="20" ry="18" fill="#fecdd3" opacity="0.5" />
              
              {/* Beine */}
              <ellipse cx="22" cy="78" rx="8" ry="10" fill="#fda4af" />
              <ellipse cx="58" cy="78" rx="8" ry="10" fill="#fda4af" />
              <ellipse cx="22" cy="85" rx="6" ry="4" fill="#fb7185" />
              <ellipse cx="58" cy="85" rx="6" ry="4" fill="#fb7185" />
              
              {/* Kopf */}
              <ellipse cx="40" cy="28" rx="22" ry="20" fill="#fda4af" />
              <circle cx="28" cy="30" r="5" fill="#fecdd3" opacity="0.4" />
              <circle cx="52" cy="30" r="5" fill="#fecdd3" opacity="0.4" />
              
              {/* Ohren */}
              <ellipse cx="22" cy="12" rx="10" ry="12" fill="#fda4af" />
              <ellipse cx="58" cy="12" rx="10" ry="12" fill="#fda4af" />
              <ellipse cx="22" cy="14" rx="6" ry="7" fill="#fb7185" opacity="0.5" />
              <ellipse cx="58" cy="14" rx="6" ry="7" fill="#fb7185" opacity="0.5" />
              
              {/* Schnauze */}
              <ellipse cx="40" cy="35" rx="12" ry="9" fill="#fb7185" className="pig-snout" />
              <ellipse cx="36" cy="35" rx="3" ry="3.5" fill="#881337" />
              <ellipse cx="44" cy="35" rx="3" ry="3.5" fill="#881337" />
              
              {/* Augen */}
              <ellipse cx="32" cy="24" rx="5" ry="6" fill="white" />
              <ellipse cx="48" cy="24" rx="5" ry="6" fill="white" />
              <circle cx="33" cy="25" r="3" fill="#1f2937" />
              <circle cx="49" cy="25" r="3" fill="#1f2937" />
              <circle cx="34" cy="24" r="1.2" fill="white" />
              <circle cx="50" cy="24" r="1.2" fill="white" />
              
              {/* Mund - pustet */}
              <ellipse cx="40" cy="42" rx="5" ry="4" fill="#881337" opacity="0.7" className="pig-mouth" />
              
              {/* Ringelschwanz */}
              <path d="M66 55 Q74 50 76 55 Q78 62 74 65 Q70 68 72 60" stroke="#fb7185" strokeWidth="3" fill="none" strokeLinecap="round" />
              
              {/* Kleeblatt auf Kopf */}
              <g transform="translate(32, -2) scale(0.5)">
                <ellipse cx="10" cy="10" rx="8" ry="10" fill="#22c55e" />
                <ellipse cx="20" cy="10" rx="8" ry="10" fill="#16a34a" />
                <ellipse cx="10" cy="20" rx="8" ry="10" fill="#16a34a" />
                <ellipse cx="20" cy="20" rx="8" ry="10" fill="#22c55e" />
                <rect x="13" y="25" width="4" height="10" fill="#15803d" rx="2" />
              </g>
            </svg>
          </div>
        </div>
      )}

      {/* ========== NEUJAHR EVENT ========== */}
      {isNewyearEvent && (
        <div className="event-scene newyear-scene">
          {/* Gelegentliches Feuerwerk */}
          <div className="newyear-fireworks">
            <div className="firework firework-1">
              <div className="spark spark-1"></div>
              <div className="spark spark-2"></div>
              <div className="spark spark-3"></div>
              <div className="spark spark-4"></div>
              <div className="spark spark-5"></div>
              <div className="spark spark-6"></div>
              <div className="spark spark-7"></div>
              <div className="spark spark-8"></div>
            </div>
            <div className="firework firework-2">
              <div className="spark spark-1"></div>
              <div className="spark spark-2"></div>
              <div className="spark spark-3"></div>
              <div className="spark spark-4"></div>
              <div className="spark spark-5"></div>
              <div className="spark spark-6"></div>
              <div className="spark spark-7"></div>
              <div className="spark spark-8"></div>
            </div>
            <div className="firework firework-3">
              <div className="spark spark-1"></div>
              <div className="spark spark-2"></div>
              <div className="spark spark-3"></div>
              <div className="spark spark-4"></div>
              <div className="spark spark-5"></div>
              <div className="spark spark-6"></div>
              <div className="spark spark-7"></div>
              <div className="spark spark-8"></div>
            </div>
          </div>
          
          {/* Glückskleeblatt unten rechts mit O2-Bubbles */}
          <div className="lucky-clover-container">
            {/* O2-Bubbles die nach oben pusten */}
            <div className="clover-o2-bubbles">
              <svg className="clover-bubble clover-bubble-1" viewBox="0 0 30 30" fill="none">
                <circle cx="15" cy="15" r="12" fill="rgba(0,180,255,0.15)" stroke="rgba(0,200,255,0.5)" strokeWidth="1.5"/>
                <ellipse cx="11" cy="11" rx="3" ry="2" fill="rgba(255,255,255,0.3)"/>
                <text x="8" y="19" fill="rgba(0,220,255,0.9)" fontSize="8" fontWeight="bold">O₂</text>
              </svg>
              <svg className="clover-bubble clover-bubble-2" viewBox="0 0 30 30" fill="none">
                <circle cx="15" cy="15" r="10" fill="rgba(0,180,255,0.12)" stroke="rgba(0,200,255,0.4)" strokeWidth="1"/>
                <ellipse cx="11" cy="11" rx="2.5" ry="1.5" fill="rgba(255,255,255,0.25)"/>
                <text x="9" y="18" fill="rgba(0,220,255,0.85)" fontSize="7" fontWeight="bold">O₂</text>
              </svg>
              <svg className="clover-bubble clover-bubble-3" viewBox="0 0 30 30" fill="none">
                <circle cx="15" cy="15" r="14" fill="rgba(0,180,255,0.18)" stroke="rgba(0,200,255,0.55)" strokeWidth="1.8"/>
                <ellipse cx="10" cy="10" rx="3.5" ry="2.2" fill="rgba(255,255,255,0.35)"/>
                <text x="7" y="20" fill="rgba(0,220,255,0.95)" fontSize="9" fontWeight="bold">O₂</text>
              </svg>
            </div>
            {/* Kleeblatt SVG */}
            <svg className="lucky-clover" viewBox="0 0 60 70" fill="none">
              <defs>
                <linearGradient id="cloverGreen1" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#4ade80"/>
                  <stop offset="100%" stopColor="#16a34a"/>
                </linearGradient>
                <filter id="cloverGlow" x="-50%" y="-50%" width="200%" height="200%">
                  <feGaussianBlur stdDeviation="1.5" result="glow"/>
                  <feMerge>
                    <feMergeNode in="glow"/>
                    <feMergeNode in="SourceGraphic"/>
                  </feMerge>
                </filter>
              </defs>
              {/* Oberes Blatt */}
              <path d="M30 8 C22 8 16 14 16 22 C16 28 22 32 30 32 C38 32 44 28 44 22 C44 14 38 8 30 8" fill="url(#cloverGreen1)" filter="url(#cloverGlow)"/>
              {/* Linkes Blatt */}
              <path d="M8 30 C8 22 14 16 22 16 C28 16 32 22 32 30 C32 38 28 44 22 44 C14 44 8 38 8 30" fill="url(#cloverGreen1)" filter="url(#cloverGlow)"/>
              {/* Rechtes Blatt */}
              <path d="M52 30 C52 22 46 16 38 16 C32 16 28 22 28 30 C28 38 32 44 38 44 C46 44 52 38 52 30" fill="url(#cloverGreen1)" filter="url(#cloverGlow)"/>
              {/* Unteres Blatt */}
              <path d="M30 52 C22 52 16 46 16 38 C16 32 22 28 30 28 C38 28 44 32 44 38 C44 46 38 52 30 52" fill="url(#cloverGreen1)" filter="url(#cloverGlow)"/>
              {/* Stiel */}
              <path d="M30 45 Q28 55 25 65" stroke="#15803d" strokeWidth="4" strokeLinecap="round" fill="none"/>
              {/* Glanzlichter auf jedem Blatt */}
              <ellipse cx="26" cy="18" rx="3" ry="2" fill="rgba(255,255,255,0.5)"/>
              <ellipse cx="18" cy="26" rx="2" ry="3" fill="rgba(255,255,255,0.5)"/>
              <ellipse cx="42" cy="26" rx="2" ry="3" fill="rgba(255,255,255,0.5)"/>
              <ellipse cx="26" cy="42" rx="3" ry="2" fill="rgba(255,255,255,0.5)"/>
            </svg>
          </div>
        </div>
      )}

      {/* ========== OSTERN EVENT ========== */}
      {/* Ostern-Elemente sind jetzt in easter-meadow-wrap */}

      {/* ========== SOMMER EVENT ========== */}
      {isSummerEvent && (
        <div className="event-scene summer-scene">
          {/* Strahlende Sonne oben rechts - Positioniert weiter links, damit sie nicht in die Uhr ragt */}
          <div className="summer-sun" style={{ right: '28%', top: 8, width: 60, height: 60, zIndex: 50 }}>
            <svg viewBox="0 0 120 120" className="sun-svg">
              <defs>
                <radialGradient id="sunGradient" cx="50%" cy="50%" r="50%">
                  <stop offset="0%" stopColor="#fff7b0"/>
                  <stop offset="40%" stopColor="#ffdd00"/>
                  <stop offset="100%" stopColor="#ffa500"/>
                </radialGradient>
                <filter id="sunGlow" x="-50%" y="-50%" width="200%" height="200%">
                  <feGaussianBlur stdDeviation="8" result="glow"/>
                  <feMerge>
                    <feMergeNode in="glow"/>
                    <feMergeNode in="glow"/>
                    <feMergeNode in="SourceGraphic"/>
                  </feMerge>
                </filter>
              </defs>
              {/* Sonnenstrahlen */}
              <g className="sun-rays">
                {[...Array(12)].map((_, i) => (
                  <line 
                    key={i}
                    x1="60" y1="60" 
                    x2={60 + Math.cos(i * 30 * Math.PI / 180) * 55} 
                    y2={60 + Math.sin(i * 30 * Math.PI / 180) * 55}
                    stroke="#ffdd00" 
                    strokeWidth="3"
                    strokeLinecap="round"
                    opacity="0.8"
                  />
                ))}
              </g>
              {/* Sonnenkern */}
              <circle cx="60" cy="60" r="28" fill="url(#sunGradient)" filter="url(#sunGlow)"/>
              {/* Glückliches Gesicht */}
              <circle cx="50" cy="55" r="3" fill="#ff8c00"/>
              <circle cx="70" cy="55" r="3" fill="#ff8c00"/>
              <path d="M48 68 Q60 78 72 68" stroke="#ff8c00" strokeWidth="3" fill="none" strokeLinecap="round"/>
            </svg>
          </div>
        </div>
      )}

      {/* ========== BLACK WEEK/FRIDAY EVENT ========== */}
      {isBlackEvent && (
        <div className="event-scene blackweek-scene">
          {/* Platzhalter - wird neu aufgebaut */}
        </div>
      )}

      {/* ========== STANDARD EVENT (Kein Event aktiv) ========== */}
      {isStandardEvent && (
        <div className="event-scene standard-scene">
          {/* Keine Maskottchen bei Standard-Event */}
        </div>
      )}
      </div>
    </div>

    {/* ========== FULLSCREEN NAVIGATION MENU - 10 Styles ========== */}
    {mainMenuOpen && (() => {
      // Event-basierte Effekte bestimmen
      const snowMenuEvents = ['winter', 'christmas', 'silvester', 'newyear'];
      const isWinterMenu = snowMenuEvents.includes(activeEvent);
      const isNewYearMenu = activeEvent === 'silvester' || activeEvent === 'newyear';
      const isChristmasMenu = activeEvent === 'christmas';
      const isSilvesterMenu = activeEvent === 'silvester';
      const isNewyearMenu = activeEvent === 'newyear';
      const isWinterOnlyMenu = activeEvent === 'winter';
      
      // Charakter bestimmen
      let menuCharacterType = null;
      if (isSilvesterMenu) menuCharacterType = 'pig';
      else if (isNewyearMenu) menuCharacterType = 'clover';
      else if (isChristmasMenu) menuCharacterType = 'santa';
      else if (isWinterOnlyMenu) menuCharacterType = 'snowman';
      
      return (
      <div className={`fullscreen-menu menu-style-${menuStyle}${systemSubmenuOpen ? ' system-open' : ''}`} ref={mainMenuRef}>
        {/* Overlay */}
        <div 
          className="fullscreen-menu-overlay" 
          onClick={() => {
            setMainMenuOpen(false);

          }}
        />
        
        {/* Menü Panel */}
        <nav className="fullscreen-menu-panel">
          {/* SCHNEE - Canvas Animation mit Ansammlung */}
          {isWinterMenu && effectSettings.headerSnow && <MenuSnowEffect />}
          
          {/* BUBBLES - nur wenn NICHT Winter-Events - O2 Style wie Header */}
          {!isWinterMenu && effectSettings.menuBubbles && (
            <>
              {[
                { left: 5, size: 12, duration: 22, delay: 0 },
                { left: 15, size: 8, duration: 18, delay: 3 },
                { left: 25, size: 18, duration: 28, delay: 6 },
                { left: 35, size: 6, duration: 16, delay: 2 },
                { left: 45, size: 10, duration: 20, delay: 9 },
                { left: 55, size: 20, duration: 30, delay: 12 },
                { left: 65, size: 8, duration: 19, delay: 5 },
                { left: 75, size: 14, duration: 25, delay: 15 },
                { left: 85, size: 10, duration: 17, delay: 8 },
                { left: 92, size: 6, duration: 14, delay: 18 },
                { left: 10, size: 16, duration: 26, delay: 10 },
                { left: 30, size: 5, duration: 15, delay: 20 },
                { left: 50, size: 12, duration: 21, delay: 14 },
                { left: 70, size: 22, duration: 32, delay: 16 },
                { left: 90, size: 8, duration: 18, delay: 22 },
              ].map((bubble, i) => (
                <div
                  key={`menu-bubble-${i}`}
                  className="menu-bubble"
                  style={{
                    left: `${bubble.left}%`,
                    width: `${bubble.size}px`,
                    height: `${bubble.size}px`,
                    animationDuration: `${bubble.duration}s`,
                    animationDelay: `${bubble.delay}s`,
                  }}
                />
              ))}
            </>
          )}
          
          {/* FEUERWERK - nur bei Silvester/Neujahr - Dynamisch animiert */}
          {isNewYearMenu && effectSettings.menuFireworks && <MenuFireworkEffect />}
          
          {/* CHARAKTER unten Mitte - Schwein/Kleeblatt/Weihnachtsmann */}
          {menuCharacterType && effectSettings.menuFigures && <MenuCharacter type={menuCharacterType} />}
          
          {/* Uhrzeit und Wetter - AUSSERHALB menu-header für korrekte Positionierung */}
          <MenuWeatherWidget />

          {/* Header - Hero Style */}
          <div className="menu-header">
            <svg viewBox="0 0 70 70" className="menu-header-icon">
              <defs>
                <linearGradient id="menuLogoGrad" x1="0%" y1="0%" x2="100%" y2="100%">
                  <stop offset="0%" stopColor="#38bdf8" />
                  <stop offset="100%" stopColor="#0284c7" />
                </linearGradient>
                <filter id="logoNeonGlow">
                  <feGaussianBlur stdDeviation="2" result="coloredBlur"/>
                  <feMerge>
                    <feMergeNode in="coloredBlur"/>
                    <feMergeNode in="SourceGraphic"/>
                  </feMerge>
                </filter>
              </defs>
              <circle cx="35" cy="35" r="32" fill="url(#menuLogoGrad)" filter="url(#logoNeonGlow)" />
              <circle cx="35" cy="35" r="32" fill="none" stroke="rgba(125,211,252,0.4)" strokeWidth="1" />
              <text x="35" y="44" textAnchor="middle" fill="white" fontSize="22" fontWeight="800" style={{fontFamily: 'system-ui, -apple-system, sans-serif'}}>O₂</text>
            </svg>
            <span className="menu-header-title">TEF {appSectionLabel}</span>
            <MenuDateDisplay />
          </div>

          {/* Menu Content */}
          <div className="menu-content">
            {/* Hero Icons Grid — dynamisch aus Settings (max 4) */}
            <MenuButtonGrid 
              activeMenuButtons={activeMenuButtons}
              onAppSectionChange={onAppSectionChange}
              setMainMenuOpen={setMainMenuOpen}
              setSystemSubmenuOpen={setSystemSubmenuOpen}
              systemSubmenuOpen={systemSubmenuOpen}
            />
            {/* Quick Links Section - nur wenn System-Panel NICHT offen */}
            {!systemSubmenuOpen && (
              <MenuSection 
                onAppSectionChange={onAppSectionChange}
                setMainMenuOpen={setMainMenuOpen}
                setSystemSubmenuOpen={setSystemSubmenuOpen}
              />
            )}

            {/* System Settings Panel - Vollbild mit Liquid Glow */}
            {systemSubmenuOpen && (
              <SystemMenuSection 
                setSystemSubmenuOpen={setSystemSubmenuOpen}
                systemActiveTab={systemActiveTab}
                setSystemActiveTab={setSystemActiveTab}
                activeEvent={activeEvent}
                onEventChange={onEventChange}
                effectSettings={effectSettings}
                handleEffectToggle={handleEffectToggle}
              />
            )}
          </div>
        </nav>
        
        {/* Particles */}
        <div className="menu-particles">
          {[...Array(12)].map((_, i) => (
            <div 
              key={i} 
              className="menu-particle"
              style={{
                '--angle': `${i * 30}deg`,
                '--delay': `${i * 0.03}s`,
                '--distance': `${80 + Math.random() * 40}px`
              }}
            />
          ))}
        </div>
      </div>
    );
    })()}
    </>
  );
}

export { Header };